import React, { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Plus, Edit2, Trash2, Package, ShoppingBag, Heart, DollarSign, X, Tag, Download, Upload, Target, HandHeart, Mail, Calendar, Zap, Settings, Wrench, Shield, Inbox, Receipt, ShieldCheck, Hammer, BookOpen, UserCheck, Users, FileText, Archive, RotateCw, Award, Clock, ClipboardCheck, KeyRound, Vote, Ticket, HandCoins, GraduationCap, Activity, MessageSquare, Send, Copy, QrCode, Coins, Bookmark } from "lucide-react";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useContentApi } from "../context/ContentContext";
import { toast } from "sonner";
import SecurityPanel from "../components/admin/SecurityPanel";
import SecurityKeysPanel from "../components/SecurityKeysPanel";
import MailboxPanel from "../components/admin/MailboxPanel";
import { SpendRequestsPanel, ContractorsPanel, CredentialsPanel } from "../components/admin/FoundationPanels";
import { SchoolContactsPanel, ExpertOpinionsPanel } from "../components/admin/FoundationExtraPanels";
import { UsersPanel } from "../components/admin/UsersPanel";
import { BooksPanel } from "../components/admin/BooksPanel";
import { StockLedgerPanel, GrantsPanel, SubscriptionsPanel, VolunteerHoursPanel, ContractorApplicationsPanel } from "../components/admin/ExtraPanels";
import { LegalRecordsPanel } from "../components/admin/LegalRecordsPanel";
import TaxLettersPanel from "../components/admin/TaxLettersPanel";
import RecordsPanel from "../components/admin/RecordsPanel";
import Phase3Panel from "../components/admin/Phase3Panels";
import Phase4_5Panel from "../components/admin/Phase4_5Panels";
import DigestPanel from "../components/admin/DigestPanel";
import BackupsPanel from "../components/admin/BackupsPanel";
import DisclosuresPanel from "../components/admin/DisclosuresPanel";
import LegalDocsPanel from "../components/admin/LegalDocsPanel";
import PollsPanel from "../components/admin/PollsPanel";
import RafflesPanel from "../components/admin/RafflesPanel";
import PublicRecordsPanel from "../components/admin/PublicRecordsPanel";
import MailAccountsPanel from "../components/admin/MailAccountsPanel";
import MailboxesPanel from "../components/admin/MailboxesPanel";
import ServicesPanel from "../components/admin/ServicesPanel";
import DirectoryPanel from "../components/admin/DirectoryPanel";
import CoursesPanel from "../components/admin/CoursesPanel";
import PayPalConfigPanel from "../components/admin/PayPalConfigPanel";
import StripeConfigPanel from "../components/admin/StripeConfigPanel";
import ShippingSettingsPanel from "../components/admin/ShippingSettingsPanel";
import DbCleanupPanel from "../components/admin/DbCleanupPanel";
import BatchCashOrderPanel from "../components/admin/BatchCashOrderPanel";
import ProductHoldsPanel from "../components/admin/ProductHoldsPanel";
import QrAnalyticsPanel from "../components/admin/QrAnalyticsPanel";
import PickupSlotsPanel from "../components/admin/PickupSlotsPanel";
import OrderDetailModal from "../components/admin/OrderDetailModal";
import { OfflinePaymentsPanel } from "../components/admin/OfflinePaymentsPanel";
import { formatApiError } from "../lib/errors";
import ImageLibraryPanel from "../components/admin/ImageLibraryPanel";
import ImagePicker from "../components/admin/ImagePicker";
import AISecretsCard from "../components/admin/AISecretsCard";

const TAB_GROUPS = [
    {
        id: "overview",
        label: "Overview",
        items: [
            { id: "stats", label: "Overview", icon: DollarSign, boardOk: true },
        ],
    },
    {
        id: "finance",
        label: "Finance",
        items: [
            { id: "books", label: "Books", icon: BookOpen, boardOk: true, boardReadOnly: true, requiresPerm: "view_financials" },
            { id: "ledger", label: "Spend Requests", icon: Receipt, boardOk: true, requiresPerm: "view_spend_requests" },
            { id: "donations", label: "Donations", icon: Heart },
            { id: "subscriptions", label: "Recurring", icon: RotateCw },
            { id: "in-kind", label: "In-kind & P2P", icon: Heart },
            { id: "tax-letters", label: "Tax letters", icon: FileText },
            { id: "annual-report", label: "Reports & Auditors", icon: FileText },
        ],
    },
    {
        id: "shop",
        label: "Shop",
        items: [
            { id: "products", label: "Products", icon: Package },
            { id: "stock", label: "Stock ledger", icon: Archive },
            { id: "orders", label: "Orders", icon: ShoppingBag },
            { id: "batch-cash", label: "Batch cash sale", icon: Coins },
            { id: "holds", label: "Holds", icon: Bookmark },
            { id: "pickup-slots", label: "Pickup slots", icon: Calendar },
            { id: "coupons", label: "Coupons", icon: Tag },
            { id: "shipping", label: "Shipping settings", icon: Package },
        ],
    },
    {
        id: "community",
        label: "Community",
        items: [
            { id: "campaigns", label: "Campaigns", icon: Target },
            { id: "grants", label: "Grants", icon: Award },
            { id: "matches", label: "Matches", icon: Zap },
            { id: "events", label: "Events", icon: Calendar },
            { id: "polls", label: "Polls", icon: Vote },
            { id: "raffles", label: "Raffles", icon: Ticket, boardOk: true },
            { id: "services", label: "Community board", icon: HandCoins },
            { id: "directory", label: "Directory", icon: BookOpen },
            { id: "courses", label: "Courses", icon: GraduationCap },
            { id: "newsletter", label: "Newsletter", icon: Mail },
            { id: "digest", label: "Digest", icon: Mail },
            { id: "mailbox", label: "Mailbox", icon: Inbox },
            { id: "mail-accounts", label: "Mail accounts", icon: Mail },
            { id: "mailboxes", label: "Public mailboxes", icon: Mail },
            { id: "paypal", label: "PayPal", icon: DollarSign },
            { id: "stripe", label: "Stripe", icon: DollarSign },
        ],
    },
    {
        id: "people",
        label: "People",
        items: [
            { id: "volunteers", label: "Volunteers", icon: HandHeart },
            { id: "volunteer-hours", label: "Volunteer hours", icon: Clock },
            { id: "contractors", label: "Contractors", icon: Hammer, boardOk: true, boardReadOnly: true, requiresPerm: "view_contractors" },
            { id: "contractor-apps", label: "Contractor apps", icon: ClipboardCheck },
            { id: "credentials", label: "Credentials", icon: ShieldCheck, boardOk: true, boardReadOnly: true, requiresPerm: "view_credentials" },
            { id: "opinions", label: "Expert opinions", icon: BookOpen, boardOk: true, boardReadOnly: true },
            { id: "contacts", label: "School contacts", icon: UserCheck, boardOk: true, boardReadOnly: true },
        ],
    },
    {
        id: "trust",
        label: "Trust & Records",
        items: [
            { id: "records", label: "Records & Automation", icon: Activity, boardOk: true, boardReadOnly: true },
            { id: "legal-records", label: "Legal records", icon: FileText, boardOk: true, boardReadOnly: true, requiresPerm: "view_legal_records" },
            { id: "legal-docs", label: "Legal documents", icon: FileText },
            { id: "public-records", label: "Public records (PDFs)", icon: FileText },
            { id: "disclosures", label: "Disclosures", icon: FileText },
        ],
    },
    {
        id: "system",
        label: "System",
        items: [
            { id: "users", label: "Users", icon: Users },
            { id: "image-library", label: "Image library", icon: Package },
            { id: "api-keys", label: "AI & Integrations keys", icon: KeyRound, boardOk: true, requiresPerm: "manage_app_secrets" },
            { id: "security", label: "Security", icon: Shield },
            { id: "backups", label: "Backups", icon: Archive },
            { id: "db-cleanup", label: "DB cleanup", icon: Wrench },
            { id: "qr-analytics", label: "QR analytics", icon: QrCode },
            { id: "settings", label: "Settings", icon: Settings },
        ],
    },
];

// Flat list still used for board filter + first-tab calc
const TABS = TAB_GROUPS.flatMap((g) => g.items);

const Admin = () => {
    const { user, loading } = useAuth();
    const isBoard = user?.role === "board";
    const userPerms = new Set(user?.permissions || []);
    const tabAllowed = (t) => {
        if (isBoard && !t.boardOk) return false;
        // Board users must also have the per-tab permission when one is defined.
        // Admins skip the perm check (effective_permissions handles superset on backend).
        if (isBoard && t.requiresPerm && !userPerms.has(t.requiresPerm)) return false;
        return true;
    };
    const visibleTabs = TABS.filter(tabAllowed);
    const defaultTab = isBoard
        ? (visibleTabs.find((t) => t.id === "ledger") ? "ledger" : (visibleTabs[0]?.id || "stats"))
        : "stats";
    const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") === "raffles" ? "raffles" : defaultTab);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    if (loading) return <div className="p-20 text-center text-[#4A5568]">Loading…</div>;
    if (!user || !["admin", "board"].includes(user.role)) return <Navigate to="/login" replace />;

    const activeTabLabel = visibleTabs.find((t) => t.id === tab)?.label || "Dashboard";

    return (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10" data-testid="admin-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-4">
                <div>
                    <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-2">{isBoard ? "Board" : "Admin"}</p>
                    <h1 className="font-heading text-3xl sm:text-4xl text-[#1C252E] font-medium tracking-tight">Dashboard</h1>
                </div>
                <p className="text-sm text-[#4A5568]">Signed in as <span className="font-medium text-[#1C252E]">{user.email}</span><span className="ml-2 inline-block px-2 py-0.5 rounded-full bg-[#E53935]/10 text-[#E53935] text-xs font-medium uppercase tracking-wider" data-testid="admin-role-badge">{user.role}</span></p>
            </div>

            {/* Mobile section header — also toggles the nav drawer */}
            <button
                onClick={() => setMobileNavOpen(!mobileNavOpen)}
                data-testid="admin-mobile-nav-toggle"
                aria-expanded={mobileNavOpen}
                className="lg:hidden w-full flex items-center justify-between bg-white border border-black/10 rounded-2xl px-4 py-3 mb-4"
            >
                <span className="font-medium text-[#1C252E]">{activeTabLabel}</span>
                <span className="text-xs text-[#4A5568]">{mobileNavOpen ? "Hide menu" : "Show menu"}</span>
            </button>

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Sidebar */}
                <aside
                    className={`${mobileNavOpen ? "block" : "hidden"} lg:block w-full lg:w-60 flex-shrink-0`}
                    data-testid="admin-sidebar"
                >
                    <nav aria-label="Admin sections" className="space-y-5 sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto pr-2">
                        {TAB_GROUPS.map((group) => {
                            const items = group.items.filter(tabAllowed);
                            if (items.length === 0) return null;
                            return (
                                <div key={group.id} data-testid={`admin-group-${group.id}`}>
                                    <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568] mb-2 px-3">
                                        {group.label}
                                    </p>
                                    <ul className="space-y-0.5">
                                        {items.map((t) => {
                                            const active = tab === t.id;
                                            return (
                                                <li key={t.id}>
                                                    <button
                                                        onClick={() => { setTab(t.id); setMobileNavOpen(false); }}
                                                        data-testid={`admin-tab-${t.id}`}
                                                        aria-current={active ? "page" : undefined}
                                                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-all ${
                                                            active
                                                                ? "bg-[#E53935] text-white font-medium shadow-sm"
                                                                : "text-[#4A5568] hover:text-[#1C252E] hover:bg-[#F4F1EA]"
                                                        }`}
                                                    >
                                                        <t.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                                                        <span className="truncate">{t.label}</span>
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            );
                        })}
                    </nav>
                </aside>

                {/* Content area */}
                <main className="flex-1 min-w-0">
                    {tab === "stats" && <StatsPanel isBoard={isBoard} />}
                    {tab === "books" && <BooksPanel />}
                    {tab === "stock" && <StockLedgerPanel />}
                    {tab === "subscriptions" && <SubscriptionsPanel />}
                    {tab === "tax-letters" && !isBoard && <TaxLettersPanel />}
                    {tab === "annual-report" && !isBoard && <Phase3Panel />}
                    {tab === "records" && <RecordsPanel />}
                    {tab === "in-kind" && !isBoard && <Phase4_5Panel />}
                    {tab === "digest" && !isBoard && <DigestPanel />}
                    {tab === "grants" && <GrantsPanel />}
                    {tab === "volunteer-hours" && <VolunteerHoursPanel />}
                    {tab === "contractor-apps" && <ContractorApplicationsPanel />}
                    {tab === "products" && !isBoard && <ProductsPanel />}
                    {tab === "orders" && !isBoard && <OrdersPanel />}
                    {tab === "batch-cash" && !isBoard && <BatchCashOrderPanel />}
                    {tab === "holds" && !isBoard && <ProductHoldsPanel />}
                    {tab === "pickup-slots" && !isBoard && <PickupSlotsPanel />}
                    {tab === "qr-analytics" && !isBoard && <QrAnalyticsPanel />}
                    {tab === "donations" && !isBoard && <DonationsPanel />}
                    {tab === "campaigns" && !isBoard && <CampaignsPanel />}
                    {tab === "matches" && !isBoard && <MatchesPanel />}
                    {tab === "coupons" && !isBoard && <CouponsPanel />}
                    {tab === "shipping" && !isBoard && <ShippingSettingsPanel />}
                    {tab === "events" && !isBoard && <EventsPanel />}
                    {tab === "polls" && !isBoard && <PollsPanel />}
                    {tab === "raffles" && <RafflesPanel />}
                    {tab === "services" && !isBoard && <ServicesPanel />}
                    {tab === "directory" && <DirectoryPanel />}
                    {tab === "courses" && !isBoard && <CoursesPanel />}
                    {tab === "volunteers" && !isBoard && <VolunteersPanel />}
                    {tab === "newsletter" && !isBoard && <NewsletterPanel />}
                    {tab === "mailbox" && !isBoard && <MailboxPanel setTab={setTab} />}
                    {tab === "ledger" && <SpendRequestsPanel isBoard={isBoard} />}
                    {tab === "contractors" && <ContractorsPanel readOnly={isBoard} />}
                    {tab === "credentials" && <CredentialsPanel readOnly={isBoard} />}
                    {tab === "legal-records" && <LegalRecordsPanel />}
                    {tab === "opinions" && <ExpertOpinionsPanel readOnly={isBoard} />}
                    {tab === "contacts" && <SchoolContactsPanel readOnly={isBoard} />}
                    {tab === "security" && !isBoard && <SecurityPanel />}
                    {tab === "backups" && !isBoard && <BackupsPanel />}
                    {tab === "db-cleanup" && !isBoard && <DbCleanupPanel />}
                    {tab === "disclosures" && !isBoard && <DisclosuresPanel />}
                    {tab === "legal-docs" && !isBoard && <LegalDocsPanel />}
                    {tab === "public-records" && !isBoard && <PublicRecordsPanel />}
                    {tab === "mail-accounts" && !isBoard && <MailAccountsPanel />}
                    {tab === "mailboxes" && !isBoard && <MailboxesPanel />}
                    {tab === "paypal" && !isBoard && <PayPalConfigPanel />}
                    {tab === "stripe" && !isBoard && <StripeConfigPanel />}
                    {tab === "users" && !isBoard && <UsersPanel />}
                    {tab === "image-library" && !isBoard && <ImageLibraryPanel />}
                    {tab === "api-keys" && <ApiKeysPanel isBoard={isBoard} />}
                    {tab === "settings" && !isBoard && <SettingsPanel />}
                </main>
            </div>
        </div>
    );
};

const SettingsPanel = () => {
    const { c, saveContent } = useContentApi();
    const [saving, setSaving] = useState(false);
    const enabled = c("maintenance.enabled", "false") === "true";

    const toggle = async () => {
        setSaving(true);
        try {
            await saveContent("maintenance.enabled", enabled ? "false" : "true");
            toast.success(enabled ? "Maintenance mode disabled — site is live" : "Maintenance mode enabled — visitors will see the maintenance page");
        } catch {
            toast.error("Failed to update");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="admin-settings-panel" className="space-y-8">
            <div className="bg-white border border-black/5 rounded-2xl p-8">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#F2D8D5] flex items-center justify-center flex-shrink-0">
                        <Wrench className="w-6 h-6 text-[#E53935]" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="font-heading text-xl text-[#1C252E] font-medium">Maintenance mode</h3>
                            <span
                                data-testid="maintenance-status-badge"
                                className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                                    enabled ? "bg-[#E53935] text-white" : "bg-green-100 text-green-700"
                                }`}
                            >
                                {enabled ? "ON · Site hidden" : "OFF · Site live"}
                            </span>
                        </div>
                        <p className="text-sm text-[#4A5568] leading-relaxed mb-5">
                            When enabled, all public visitors see the maintenance page instead of the site. Admins (you)
                            and the <code className="text-xs bg-[#F4F1EA] px-1.5 py-0.5 rounded">/admin</code> +{" "}
                            <code className="text-xs bg-[#F4F1EA] px-1.5 py-0.5 rounded">/login</code> routes always
                            stay accessible so you can toggle back.
                        </p>
                        <div className="flex items-center gap-3 flex-wrap">
                            <button
                                onClick={toggle}
                                disabled={saving}
                                data-testid="maintenance-toggle-btn"
                                className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-all hover:-translate-y-0.5 disabled:opacity-60 ${
                                    enabled
                                        ? "bg-green-600 hover:bg-green-700 text-white"
                                        : "bg-[#E53935] hover:bg-[#D32F2F] text-white"
                                }`}
                            >
                                {saving ? "Saving…" : enabled ? "Turn maintenance OFF" : "Turn maintenance ON"}
                            </button>
                            <a
                                href="/maintenance-preview"
                                target="_blank"
                                rel="noreferrer"
                                data-testid="maintenance-preview-link"
                                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium border border-black/10 hover:bg-[#F4F1EA] transition-all"
                            >
                                Preview & edit maintenance page →
                            </a>
                        </div>
                        <p className="text-xs text-[#4A5568] mt-4">
                            Tip: while ON, the rest of the site stays editable for you — visit any public page from{" "}
                            <a href="/" className="underline text-[#E53935] hover:text-[#D32F2F]">your homepage</a>{" "}
                            and you'll see the live site (only visitors get the maintenance page). To edit the maintenance copy itself, click <strong>"Preview & edit maintenance page"</strong> above — it opens in a new tab with inline edit fields.
                        </p>
                    </div>
                </div>
            </div>

            <div className="bg-white border border-black/5 rounded-2xl p-8">
                <h3 className="font-heading text-xl text-[#1C252E] font-medium mb-2">Coming-soon page</h3>
                <p className="text-sm text-[#4A5568] leading-relaxed mb-4">
                    A standalone placeholder route you can link to anywhere on the site. Share it on social or use it
                    for sections that aren't built yet.
                </p>
                <a
                    href="/coming-soon"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="admin-coming-soon-link"
                    className="inline-flex items-center gap-2 text-sm font-medium text-[#E53935] hover:underline"
                >
                    Preview /coming-soon →
                </a>
            </div>

            <ShopComingSoonToggle />

            <PdfExportCard />

            {/* FIDO2 / WebAuthn security keys — admins enroll here so the Step-up
                prompt on App Secrets / Permissions changes can verify them. */}
            <SecurityKeysPanel />
        </div>
    );
};


// Toggle the Spirit Shop into "Coming soon" mode. When enabled, /shop renders
// the placeholder banner instead of products; the rest of the site stays live.
const ShopComingSoonToggle = () => {
    const { c, saveContent } = useContentApi();
    const [saving, setSaving] = useState(false);
    const enabled = c("shop.coming_soon", "false") === "true";
    const toggle = async () => {
        setSaving(true);
        try {
            await saveContent("shop.coming_soon", enabled ? "false" : "true");
            toast.success(enabled ? "Spirit Shop is now live" : "Spirit Shop hidden — visitors see Coming Soon");
        } catch {
            toast.error("Failed to update");
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="bg-white border border-black/5 rounded-2xl p-8" data-testid="shop-coming-soon-card">
            <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#F2D8D5] flex items-center justify-center flex-shrink-0">
                    <ShoppingBag className="w-6 h-6 text-[#E53935]" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="font-heading text-xl text-[#1C252E] font-medium">Spirit Shop · Coming Soon mode</h3>
                        <span
                            data-testid="shop-coming-soon-status"
                            className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                                enabled ? "bg-[#E53935] text-white" : "bg-green-100 text-green-700"
                            }`}
                        >
                            {enabled ? "ON · Shop hidden" : "OFF · Shop live"}
                        </span>
                    </div>
                    <p className="text-sm text-[#4A5568] leading-relaxed mb-5">
                        When ON, anyone visiting <code className="text-xs bg-[#F4F1EA] px-1.5 py-0.5 rounded">/shop</code>{" "}
                        sees a "Coming soon" page with a Donate button instead of products. Use this while products are still being added or photos are pending.
                    </p>
                    <button
                        onClick={toggle}
                        disabled={saving}
                        data-testid="shop-coming-soon-toggle-btn"
                        className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-all hover:-translate-y-0.5 disabled:opacity-60 ${
                            enabled
                                ? "bg-green-600 hover:bg-green-700 text-white"
                                : "bg-[#E53935] hover:bg-[#D32F2F] text-white"
                        }`}
                    >
                        {saving ? "Saving…" : enabled ? "Show shop · turn OFF" : "Hide shop · turn ON"}
                    </button>
                </div>
            </div>
        </div>
    );
};


const ApiKeysPanel = ({ isBoard }) => {
    return (
        <div className="space-y-6" data-testid="admin-api-keys-panel">
            <div className="bg-white border border-black/5 rounded-2xl p-8">
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#F2D8D5] flex items-center justify-center flex-shrink-0">
                        <KeyRound className="w-6 h-6 text-[#E53935]" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-xl text-[#1C252E] font-medium mb-2">AI &amp; Integrations keys</h3>
                        <p className="text-sm text-[#4A5568] leading-relaxed">
                            Manage runtime API keys for AI compose, object storage, and Google / Facebook sign-in.
                            Keys are stored encrypted at rest; only the last 4 characters are ever displayed.
                            {isBoard && (
                                <span className="block mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                    You have <strong>Manage AI &amp; Integrations keys</strong> permission — please treat
                                    these credentials with the same care as financial records.
                                </span>
                            )}
                        </p>
                    </div>
                </div>
            </div>
            <AISecretsCard />
            <AppSecretsAuditCard />
        </div>
    );
};


const AppSecretsAuditCard = () => {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [verifyResult, setVerifyResult] = useState(null);
    const [verifying, setVerifying] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/app-secrets/audit?limit=50");
            setEntries(data?.entries || []);
        } catch {
            setEntries([]);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const verify = async () => {
        setVerifying(true);
        try {
            const { data } = await api.post("/admin/app-secrets/audit/verify");
            setVerifyResult(data);
            if (data.ok) {
                toast.success(`Audit chain intact — ${data.total_rows} rows verified`);
            } else {
                toast.error(`Tamper detected: ${data.reason}`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Verify failed");
        } finally {
            setVerifying(false);
        }
    };

    const fmtTime = (iso) => {
        try {
            const d = new Date(iso);
            return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
        } catch {
            return iso;
        }
    };
    const actionBadge = (a, success) => {
        if (a === "set") return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-700">Updated</span>;
        if (a === "clear") return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700">Cleared</span>;
        if (a === "read") return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600">Read</span>;
        if (a === "denied") return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-orange-100 text-orange-700">Denied</span>;
        if (a === "test")
            return success
                ? <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700">Test OK</span>
                : <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700">Test Failed</span>;
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-700">{a}</span>;
    };

    return (
        <div className="bg-white border border-black/5 rounded-2xl p-8" data-testid="app-secrets-audit-card">
            <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className="w-12 h-12 rounded-full bg-[#F2D8D5] flex items-center justify-center flex-shrink-0">
                        <FileText className="w-6 h-6 text-[#E53935]" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-xl text-[#1C252e] font-medium mb-1">Audit log</h3>
                        <p className="text-sm text-[#4A5568] leading-relaxed">
                            Who changed which key &amp; when. Only the last-4 characters and source are
                            recorded — cleartext values are <strong>never</strong> stored.
                        </p>
                    </div>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    data-testid="app-secrets-audit-refresh"
                    className="inline-flex items-center gap-2 text-sm font-medium text-[#E53935] hover:underline disabled:opacity-60"
                >
                    <RotateCw className="w-3.5 h-3.5" strokeWidth={2} /> Refresh
                </button>
            </div>

            {/* Tamper-evidence integrity check (SOC 2 CC7.3) */}
            <div className="flex flex-wrap items-center gap-3 mb-5 p-3 rounded-xl bg-[#F4F1EA] border border-black/5" data-testid="audit-verify-panel">
                <Shield className="w-4 h-4 text-[#E53935] flex-shrink-0" strokeWidth={2} />
                <span className="text-xs text-[#4A5568] flex-1 min-w-[200px]">
                    Audit rows are hash-chained — any deletion or edit is detectable.
                </span>
                {verifyResult && (
                    <span
                        data-testid="audit-verify-result"
                        className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                            verifyResult.ok
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                        }`}
                    >
                        {verifyResult.ok ? `✓ ${verifyResult.total_rows} rows intact` : `✗ ${verifyResult.reason}`}
                    </span>
                )}
                <button
                    onClick={verify}
                    disabled={verifying}
                    data-testid="audit-verify-btn"
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-white border border-black/10 hover:border-[#E53935] hover:text-[#E53935] transition-colors disabled:opacity-60"
                >
                    {verifying ? "Verifying…" : "Verify integrity"}
                </button>
            </div>

            {loading ? (
                <p className="text-sm text-[#4A5568] italic">Loading…</p>
            ) : entries.length === 0 ? (
                <p className="text-sm text-[#4A5568] italic" data-testid="app-secrets-audit-empty">
                    No changes recorded yet. The next "Save", "Clear", or "Test" will land here.
                </p>
            ) : (
                <>
                    {/* Mobile (<sm): stacked cards */}
                    <div className="sm:hidden space-y-2.5" data-testid="app-secrets-audit-mobile">
                        {entries.map((e) => (
                            <div key={e.id} className="border border-black/5 rounded-xl p-3 bg-[#FAFAFA]" data-testid={`app-secrets-audit-card-${e.id}`}>
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="min-w-0">
                                        <p className="font-mono text-xs text-[#1C252E] truncate">{e.key}</p>
                                        <p className="text-[10px] text-[#4A5568] mt-0.5">{fmtTime(e.changed_at)}</p>
                                    </div>
                                    <div className="flex-shrink-0">{actionBadge(e.action, e.success)}</div>
                                </div>
                                <div className="flex items-center justify-between gap-2 flex-wrap text-xs pt-2 border-t border-black/5">
                                    <div className="font-mono text-xs">
                                        {e.before_last4 && e.after_last4 && e.before_last4 !== e.after_last4 ? (
                                            <span><span className="text-[#4A5568]">…{e.before_last4}</span> → <span>…{e.after_last4}</span></span>
                                        ) : e.after_last4 ? (
                                            <span>…{e.after_last4}</span>
                                        ) : e.before_last4 ? (
                                            <span className="text-[#4A5568] line-through">…{e.before_last4}</span>
                                        ) : (
                                            <span className="text-[#4A5568]">—</span>
                                        )}
                                    </div>
                                    <div className="text-right text-[10px]">
                                        <div className="text-[#1C252E] truncate max-w-[180px]">{e.changed_by_email || "—"}</div>
                                        {e.changed_by_role && (
                                            <div className="uppercase tracking-wider text-[#4A5568]">{e.changed_by_role}{e.request_ip ? ` · ${e.request_ip}` : ""}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Desktop (≥sm): table */}
                    <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full text-sm" data-testid="app-secrets-audit-table">
                            <thead className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#4A5568] border-b border-black/5">
                                <tr>
                                    <th className="text-left py-2 pr-4">When</th>
                                    <th className="text-left py-2 pr-4">Key</th>
                                    <th className="text-left py-2 pr-4">Action</th>
                                    <th className="text-left py-2 pr-4">Last 4</th>
                                    <th className="text-left py-2 pr-4">By</th>
                                </tr>
                            </thead>
                            <tbody className="text-[#1C252E]">
                                {entries.map((e) => (
                                    <tr key={e.id} className="border-b border-black/5 last:border-0">
                                        <td className="py-2.5 pr-4 text-xs text-[#4A5568] whitespace-nowrap">{fmtTime(e.changed_at)}</td>
                                        <td className="py-2.5 pr-4 font-mono text-xs">{e.key}</td>
                                        <td className="py-2.5 pr-4">{actionBadge(e.action, e.success)}</td>
                                        <td className="py-2.5 pr-4 font-mono text-xs">
                                            {e.before_last4 && e.after_last4 && e.before_last4 !== e.after_last4 ? (
                                                <span><span className="text-[#4A5568]">…{e.before_last4}</span> → <span>…{e.after_last4}</span></span>
                                            ) : e.after_last4 ? (
                                                <span>…{e.after_last4}</span>
                                            ) : e.before_last4 ? (
                                                <span className="text-[#4A5568] line-through">…{e.before_last4}</span>
                                            ) : (
                                                <span className="text-[#4A5568]">—</span>
                                            )}
                                        </td>
                                        <td className="py-2.5 pr-4 text-xs">
                                            <div className="text-[#1C252E]">{e.changed_by_email || "—"}</div>
                                            {e.changed_by_role && (
                                                <div className="text-[10px] uppercase tracking-wider text-[#4A5568]">{e.changed_by_role}{e.request_ip ? ` · ${e.request_ip}` : ""}</div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};


const PdfExportCard = () => {
    const [busy, setBusy] = useState(false);
    const [includeSpend, setIncludeSpend] = useState(true);

    const run = async () => {
        setBusy(true);
        try {
            const res = await api.post(
                `/admin/export-public-pdf?include_spend_details=${includeSpend}`,
                null,
                { responseType: "blob", timeout: 180000 }
            );
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const stamp = new Date().toISOString().slice(0, 10);
            const a = document.createElement("a");
            a.href = url;
            a.download = `reddog-public-pages-${stamp}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 4000);
            const rendered = res.headers["x-pages-rendered"];
            const failed = res.headers["x-pages-failed"];
            toast.success(`PDF ready — ${rendered} pages rendered${failed && failed !== "0" ? `, ${failed} failed` : ""}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Export failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-white border border-black/5 rounded-2xl p-8" data-testid="admin-pdf-export-card">
            <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#F2D8D5] flex items-center justify-center flex-shrink-0">
                    <Download className="w-6 h-6 text-[#E53935]" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-xl text-[#1C252E] font-medium mb-2">Export public pages to PDF</h3>
                    <p className="text-sm text-[#4A5568] leading-relaxed mb-4">
                        Renders Home, About, Donate, Shop, and the Public Ledger using a headless browser and
                        combines them into a single PDF — perfect for board packets, grant applications, or
                        printing for in-person events.
                    </p>
                    <label className="flex items-center gap-2 text-sm text-[#1C252E] mb-5 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={includeSpend}
                            onChange={(e) => setIncludeSpend(e.target.checked)}
                            data-testid="pdf-export-include-spend"
                        />
                        <span>Also include each paid spend-detail page (up to 50)</span>
                    </label>
                    <button
                        onClick={run}
                        disabled={busy}
                        data-testid="pdf-export-btn"
                        className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] disabled:opacity-60 text-white rounded-full px-6 py-3 text-sm font-medium transition-all hover:-translate-y-0.5"
                    >
                        <Download className="w-4 h-4" strokeWidth={2} />
                        {busy ? "Rendering pages (~30-90s)…" : "Generate PDF"}
                    </button>
                    <p className="text-xs text-[#4A5568] mt-3">
                        Heads up: This takes 30–90 seconds depending on how many pages are included.
                    </p>
                </div>
            </div>
        </div>
    );
};

const PaymentHealthWidget = () => {
    const [state, setState] = useState(null);
    const [running, setRunning] = useState(false);

    const load = () => api.get("/admin/payments/health").then((r) => setState(r.data)).catch(() => setState({ error: true }));
    useEffect(() => { load(); }, []);

    const runNow = async () => {
        setRunning(true);
        try {
            const r = await api.post("/admin/payments/health/run-now");
            setState(r.data);
            toast.success("Health check ran");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Health check failed");
        } finally {
            setRunning(false);
        }
    };

    if (!state) return null;
    if (state.error) return null; // silently hide for board members / non-owners

    const providers = state.providers || {};
    const threshold = state.fail_threshold || 3;
    const intervalMin = Math.round((state.interval_seconds || 900) / 60);

    const providerRow = (name) => {
        const p = providers[name] || { consecutive_failures: 0, flipped: false, last_error: "" };
        const healthy = p.consecutive_failures === 0 && !p.flipped;
        const warning = p.consecutive_failures > 0 && !p.flipped;
        const down = p.flipped;
        const color = down ? "#E53935" : warning ? "#F59E0B" : "#2E7D32";
        const label = down ? "Auto-disabled" : warning ? `${p.consecutive_failures}/${threshold} fails` : "Healthy";

        return (
            <div key={name} className="flex items-center gap-3" data-testid={`payment-health-${name}`}>
                <span
                    className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ background: color, boxShadow: `0 0 0 3px ${color}22` }}
                    aria-hidden
                />
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1C252E] capitalize">{name}</p>
                    <p className="text-[11px] text-[#4A5568] truncate" data-testid={`payment-health-${name}-status`}>
                        {label}{p.last_error ? ` · ${p.last_error.slice(0, 60)}` : ""}
                    </p>
                </div>
                {down && (
                    <span className="text-[10px] uppercase tracking-wider bg-[#E53935] text-white rounded-full px-2 py-0.5 font-bold">
                        Kill-switch on
                    </span>
                )}
                {healthy && (
                    <span className="text-[10px] uppercase tracking-wider bg-[#F4F1EA] text-[#4A5568] rounded-full px-2 py-0.5 font-medium">
                        OK
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className="bg-white border border-black/5 rounded-2xl p-6" data-testid="admin-payment-health-widget">
            <div className="flex items-start justify-between mb-4 gap-3">
                <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#E53935] font-bold">Payment ops</p>
                    <h3 className="font-heading text-lg text-[#1C252E] font-medium mt-0.5">Provider health</h3>
                    <p className="text-[11px] text-[#4A5568] mt-1">Checks every {intervalMin} min · auto-disables after {threshold} consecutive auth fails.</p>
                </div>
                <button
                    onClick={runNow}
                    disabled={running}
                    data-testid="admin-payment-health-run-now"
                    aria-label="Run health check now"
                    className="inline-flex items-center gap-1.5 text-xs bg-[#F4F1EA] hover:bg-[#F2D8D5] rounded-full px-3 py-1.5 text-[#1C252E] font-medium disabled:opacity-60"
                >
                    <RotateCw className={`w-3.5 h-3.5 ${running ? "animate-spin" : ""}`} strokeWidth={2} />
                    {running ? "Checking…" : "Run now"}
                </button>
            </div>
            <div className="space-y-3">
                {providerRow("stripe")}
                {providerRow("paypal")}
            </div>
        </div>
    );
};

const StatsPanel = ({ isBoard }) => {
    const [stats, setStats] = useState(null);
    const [pl, setPl] = useState(null);
    const [plPeriod, setPlPeriod] = useState("lifetime");

    useEffect(() => {
        api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    }, []);
    useEffect(() => {
        if (isBoard) return;
        api.get(`/admin/merch-pl?period=${plPeriod}`).then((r) => {
            setPl(r.data);
        }).catch(() => {});
    }, [plPeriod, isBoard]);

    if (!stats) return <div className="text-[#4A5568]" data-testid="admin-stats-loading">Loading…</div>;

    const cards = [
        { label: "Total Products", value: stats.products_count, icon: Package },
        { label: "Total Orders", value: stats.orders_count, icon: ShoppingBag },
        { label: "Order Revenue", value: `$${stats.orders_revenue.toFixed(2)}`, icon: DollarSign },
        { label: "Donations Raised", value: `$${stats.donations_total.toFixed(2)}`, icon: Heart },
    ];

    return (
        <div className="space-y-10">
            {!isBoard && <PaymentHealthWidget />}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="admin-stats-grid">
                {cards.map((c) => (
                    <div key={c.label} className="bg-white border border-black/5 rounded-2xl p-6" data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
                        <div className="w-10 h-10 bg-[#F2D8D5] rounded-full flex items-center justify-center mb-4">
                            <c.icon className="w-5 h-5 text-[#E53935]" strokeWidth={1.5} />
                        </div>
                        <p className="text-xs uppercase tracking-wider text-[#4A5568] mb-1">{c.label}</p>
                        <p className="font-heading text-3xl text-[#1C252E] font-medium">{c.value}</p>
                    </div>
                ))}
            </div>

            {!isBoard && pl && (
                <div className="bg-white border border-black/5 rounded-3xl overflow-hidden" data-testid="admin-merch-pl-card">
                    <div className="px-8 pt-7 pb-5 border-b border-black/5 flex items-end justify-between flex-wrap gap-3">
                        <div>
                            <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#E53935] mb-2">Merch P&amp;L</p>
                            <h3 className="font-heading text-2xl text-[#1C252E] font-medium">Where every dollar of merch revenue goes</h3>
                            <p className="text-xs text-[#4A5568] mt-1.5">Server-authoritative breakdown using cost-at-sale snapshots. {pl.order_count} paid order(s) included.</p>
                        </div>
                        <div className="flex items-center gap-2" data-testid="admin-merch-pl-period">
                            {[
                                { v: "week", l: "7 days" },
                                { v: "month", l: "This month" },
                                { v: "ytd", l: "YTD" },
                                { v: "lifetime", l: "Lifetime" },
                            ].map((p) => (
                                <button
                                    key={p.v}
                                    onClick={() => setPlPeriod(p.v)}
                                    data-testid={`admin-pl-period-${p.v}`}
                                    className={`text-xs px-3 py-1.5 rounded-full transition-all ${plPeriod === p.v ? "bg-[#1C252E] text-white" : "bg-[#F4F1EA] text-[#4A5568] hover:bg-[#E53935] hover:text-white"}`}
                                >
                                    {p.l}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-black/5">
                        <div className="p-6" data-testid="admin-pl-revenue">
                            <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-1">Revenue</p>
                            <p className="font-heading text-3xl text-[#1C252E] font-medium">${pl.revenue.toLocaleString()}</p>
                            <p className="text-[11px] text-[#4A5568] mt-1">Total merch sales</p>
                        </div>
                        <div className="p-6 bg-[#F4F1EA]/40" data-testid="admin-pl-cogs">
                            <p className="text-xs uppercase tracking-wider font-bold text-amber-700 mb-1">−&nbsp;Cost recovered</p>
                            <p className="font-heading text-3xl text-[#1C252E] font-medium">${pl.cogs.toLocaleString()}</p>
                            <p className="text-[11px] text-[#4A5568] mt-1">Restocks the shop · COGS</p>
                        </div>
                        <div className="p-6 bg-[#F2D8D5]/40 border-l-4 border-[#E53935]" data-testid="admin-pl-net-to-gsf">
                            <p className="text-xs uppercase tracking-wider font-bold text-[#E53935] mb-1">=&nbsp;Net to GSF</p>
                            <p className="font-heading text-3xl text-[#1C252E] font-medium">${pl.net_to_gsf.toLocaleString()}</p>
                            <p className="text-[11px] text-[#4A5568] mt-1">Funds approved programs</p>
                        </div>
                        <div className="p-6" data-testid="admin-pl-margin">
                            <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-1">Gross margin</p>
                            <p className={`font-heading text-3xl font-medium ${pl.gross_margin_pct >= 50 ? "text-green-700" : pl.gross_margin_pct >= 25 ? "text-amber-700" : "text-red-700"}`}>
                                {pl.gross_margin_pct.toFixed(1)}%
                            </p>
                            <p className="text-[11px] text-[#4A5568] mt-1">Net to GSF ÷ revenue</p>
                        </div>
                    </div>

                    <div className="px-8 py-4 bg-white border-t border-black/5 text-[11px] text-[#4A5568] leading-relaxed" data-testid="admin-pl-example">
                        <strong>How it works:</strong> A product that costs the foundation <strong>$2.00</strong> and sells for <strong>$10.00</strong> →
                        cost recovered <strong>$2.00</strong> · net to GSF <strong>$8.00</strong>. Every dollar of margin flows directly to the General Support Fund — no reserve, no buffer.
                        Sponsorship items (cost=$0) flow 100% to their target campaign and are excluded from this rollup. Cancelled orders are voided out.
                    </div>
                </div>
            )}
        </div>
    );
};

const emptyProduct = {
    name: "",
    description: "",
    price: "",
    cost: "",
    sku: "",
    category: "apparel",
    image_url: "",
    images: [],
    stock: 100,
    weight_oz: 4,
    length_in: 10,
    width_in: 8,
    height_in: 2,
    featured: false,
    active: true,
};

// Reusable labeled-field wrapper for the product form
const Field = ({ label, helper, required, children, testid }) => (
    <div data-testid={testid}>
        <label className="block text-xs uppercase tracking-[0.12em] font-bold text-[#1C252E] mb-1.5">
            {label}{required && <span className="text-[#E53935] ml-1">*</span>}
        </label>
        {children}
        {helper && <p className="mt-1.5 text-[11px] text-[#4A5568] leading-snug">{helper}</p>}
    </div>
);

const Section = ({ title, subtitle, children }) => (
    <div className="border-t border-black/5 pt-5 first:border-0 first:pt-0">
        <div className="mb-3">
            <h4 className="text-sm font-semibold text-[#1C252E]">{title}</h4>
            {subtitle && <p className="text-[11px] text-[#4A5568] mt-0.5">{subtitle}</p>}
        </div>
        <div className="space-y-4">{children}</div>
    </div>
);

const ProductsPanel = () => {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyProduct);
    const [showForm, setShowForm] = useState(false);
    const [showCats, setShowCats] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [showPicker, setShowPicker] = useState(false);

    const loadProducts = () => api.get("/admin/products").then((r) => setItems(r.data)).catch(() => {});
    const loadCategories = () => api.get("/admin/categories").then((r) => setCategories(r.data || [])).catch(() => {});
    useEffect(() => { loadProducts(); loadCategories(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm({ ...emptyProduct, category: categories[0]?.slug || "apparel" });
        setShowForm(true);
    };
    const openEdit = (p) => {
        setEditing(p);
        setForm({
            name: p.name || "",
            description: p.description || "",
            price: p.price ?? "",
            cost: p.cost ?? "",
            sku: p.sku || "",
            category: p.category || "apparel",
            image_url: p.image_url || "",
            images: Array.isArray(p.images) ? p.images : [],
            stock: p.stock ?? 0,
            weight_oz: p.weight_oz ?? 4,
            length_in: p.length_in ?? 10,
            width_in: p.width_in ?? 8,
            height_in: p.height_in ?? 2,
            featured: !!p.featured,
            active: p.active !== false,
        });
        setShowForm(true);
    };
    const close = () => setShowForm(false);

    const save = async (e) => {
        e.preventDefault();
        const priceN = parseFloat(form.price);
        const costN = parseFloat(form.cost) || 0;
        if (Number.isNaN(priceN) || priceN < 0) {
            toast.error("Sale price must be a number ≥ 0");
            return;
        }
        if (costN > priceN && form.category !== "donation") {
            if (!window.confirm(`Cost ($${costN.toFixed(2)}) is higher than sale price ($${priceN.toFixed(2)}) — this product will sell at a loss. Continue?`)) return;
        }
        const body = {
            ...form,
            price: priceN,
            cost: costN,
            sku: (form.sku || "").trim() || null,
            stock: parseInt(form.stock || 0, 10) || 0,
            weight_oz: parseFloat(form.weight_oz) || 4,
            length_in: parseFloat(form.length_in) || 10,
            width_in: parseFloat(form.width_in) || 8,
            height_in: parseFloat(form.height_in) || 2,
        };
        try {
            if (editing) {
                await api.put(`/products/${editing.id}`, body);
                toast.success("Product updated");
            } else {
                await api.post("/products", body);
                toast.success("Product created");
            }
            close();
            loadProducts();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this product? This cannot be undone.")) return;
        try {
            await api.delete(`/products/${id}`);
            toast.success("Deleted");
            loadProducts();
        } catch {
            toast.error("Delete failed");
        }
    };

    // Live margin computation for the form
    const priceN = parseFloat(form.price) || 0;
    const costN = parseFloat(form.cost) || 0;
    const profitPerUnit = priceN - costN;
    const marginPct = priceN > 0 ? (profitPerUnit / priceN) * 100 : 0;

    return (
        <div data-testid="admin-products-panel">
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Products</h2>
                    <p className="text-sm text-[#4A5568] mt-1">{items.length} total · {items.filter((p) => p.active !== false).length} active on storefront</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => setShowCats(true)}
                        data-testid="admin-manage-categories-btn"
                        className="inline-flex items-center gap-2 text-sm font-medium text-[#1C252E] hover:text-[#E53935] px-4 py-2.5 rounded-full border border-black/10 hover:border-[#E53935] transition-all"
                    >
                        <Tag className="w-4 h-4" strokeWidth={1.5} />
                        Manage categories
                    </button>
                    <a
                        href={`${API}/admin/products-template.csv`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="admin-products-template-link"
                        className="inline-flex items-center gap-2 text-sm font-medium text-[#1C252E] hover:text-[#E53935] px-4 py-2.5 rounded-full border border-black/10 hover:border-[#E53935] transition-all"
                    >
                        <Download className="w-4 h-4" strokeWidth={1.5} />
                        CSV template
                    </a>
                    <button
                        type="button"
                        onClick={async () => {
                            const token = localStorage.getItem("token");
                            try {
                                const r = await fetch(`${API}/admin/products/qr-sheet.pdf`, { headers: { Authorization: `Bearer ${token}` } });
                                if (!r.ok) throw new Error(await r.text());
                                const blob = await r.blob();
                                window.open(URL.createObjectURL(blob), "_blank");
                                toast.success("QR sheet ready");
                            } catch (e) {
                                toast.error(`QR sheet failed: ${e.message || e}`);
                            }
                        }}
                        data-testid="admin-products-qr-sheet-btn"
                        title="Print a laminate-ready sheet with every product's QR code"
                        className="inline-flex items-center gap-2 text-sm font-medium text-[#1C252E] hover:text-[#E53935] px-4 py-2.5 rounded-full border border-black/10 hover:border-[#E53935] transition-all"
                    >
                        <QrCode className="w-4 h-4" strokeWidth={1.5} />
                        Print QR sheet
                    </button>
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-[#1C252E] px-4 py-2.5 rounded-full border border-black/10 cursor-pointer hover:border-[#E53935] transition-all" data-testid="admin-products-import-label">
                        <Upload className="w-4 h-4" strokeWidth={1.5} />
                        {importing ? "Importing…" : "Import CSV"}
                        <input
                            type="file"
                            accept=".csv,text/csv"
                            className="hidden"
                            data-testid="admin-products-import-input"
                            onChange={async (e) => {
                                const f = e.target.files?.[0];
                                if (!f) return;
                                setImporting(true);
                                try {
                                    const fd = new FormData();
                                    fd.append("file", f);
                                    const { data } = await api.post("/admin/products-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                                    toast.success(`Imported ${data.created} product(s)${data.errors.length ? ` · ${data.errors.length} skipped` : ""}`);
                                    loadProducts();
                                } catch (err) {
                                    toast.error(err.response?.data?.detail || "Import failed");
                                } finally {
                                    setImporting(false);
                                    e.target.value = "";
                                }
                            }}
                        />
                    </label>
                    <button
                        onClick={openCreate}
                        data-testid="admin-add-product-btn"
                        className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-6 py-2.5 text-sm font-medium transition-all"
                    >
                        <Plus className="w-4 h-4" strokeWidth={2} />
                        Add product
                    </button>
                </div>
            </div>

            <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-products-table">
                        <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                            <tr>
                                <th className="text-left px-4 py-3 font-medium">Product</th>
                                <th className="text-left px-4 py-3 font-medium">SKU</th>
                                <th className="text-left px-4 py-3 font-medium">Category</th>
                                <th className="text-right px-4 py-3 font-medium">Cost</th>
                                <th className="text-right px-4 py-3 font-medium">Sale price</th>
                                <th className="text-right px-4 py-3 font-medium">Margin</th>
                                <th className="text-right px-4 py-3 font-medium">Stock</th>
                                <th className="text-center px-4 py-3 font-medium">Status</th>
                                <th className="text-right px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-black/5">
                            {items.length === 0 && (
                                <tr><td colSpan={9} className="px-6 py-12 text-center text-[#4A5568]">No products yet. Click "Add product" to create one.</td></tr>
                            )}
                            {items.map((p) => {
                                const cost = p.cost || 0;
                                const margin = p.price > 0 ? ((p.price - cost) / p.price) * 100 : 0;
                                const isSponsor = p.category === "donation";
                                return (
                                <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className={p.active === false ? "bg-gray-50/50" : ""}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <img src={p.image_url || undefined} alt="" className="w-10 h-10 rounded-lg object-cover bg-[#F4F1EA] flex-shrink-0" />
                                            <div>
                                                <p className="font-medium text-[#1C252E]">{p.name}</p>
                                                {p.featured && <span className="inline-block mt-0.5 text-[10px] uppercase tracking-wider font-bold text-[#E53935]">Featured</span>}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-[#4A5568] font-mono text-xs">{p.sku || "—"}</td>
                                    <td className="px-4 py-3 text-[#4A5568]">
                                        {categories.find((c) => c.slug === p.category)?.label || p.category}
                                    </td>
                                    <td className="px-4 py-3 text-right text-[#4A5568] font-mono">{isSponsor ? "—" : `$${cost.toFixed(2)}`}</td>
                                    <td className="px-4 py-3 text-right text-[#1C252E] font-mono font-medium">${p.price.toFixed(2)}</td>
                                    <td className={`px-4 py-3 text-right font-mono text-xs ${isSponsor ? "text-[#4A5568]" : margin >= 50 ? "text-green-700" : margin >= 25 ? "text-amber-700" : "text-red-700"}`}>
                                        {isSponsor ? "100%" : `${margin.toFixed(0)}%`}
                                    </td>
                                    <td className="px-4 py-3 text-right text-[#4A5568]">{p.stock}</td>
                                    <td className="px-4 py-3 text-center">
                                        {p.active === false ? (
                                            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-gray-200 text-gray-600">Hidden</span>
                                        ) : (
                                            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-green-100 text-green-700">Active</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        <a
                                            href={`${API}/products/${p.id}/qr.png?size=512`}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`admin-qr-${p.id}`}
                                            className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#4A5568] inline-block"
                                            aria-label="Download QR code"
                                            title="Download QR code for this product"
                                        >
                                            <QrCode className="w-4 h-4" strokeWidth={1.5} />
                                        </a>
                                        <button
                                            onClick={() => openEdit(p)}
                                            data-testid={`admin-edit-${p.id}`}
                                            className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#4A5568]"
                                            aria-label="Edit"
                                        >
                                            <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                                        </button>
                                        <button
                                            onClick={() => remove(p.id)}
                                            data-testid={`admin-delete-${p.id}`}
                                            className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#E53935]"
                                            aria-label="Delete"
                                        >
                                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                        </button>
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {showForm && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={close} data-testid="admin-product-modal">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center px-8 py-5 border-b border-black/5">
                            <div>
                                <h3 className="font-heading text-2xl text-[#1C252E] font-medium">{editing ? "Edit product" : "New product"}</h3>
                                {editing && <p className="text-xs text-[#4A5568] mt-1">ID: <span className="font-mono">{editing.id}</span></p>}
                            </div>
                            <button onClick={close} className="p-2 hover:bg-[#F4F1EA] rounded-full" aria-label="Close"><X className="w-4 h-4" /></button>
                        </div>
                        <form onSubmit={save} className="overflow-y-auto px-8 py-6 space-y-6 flex-1" data-testid="admin-product-form">
                            {/* Basic info */}
                            <Section title="Basic info" subtitle="What the product is called and what it does.">
                                <Field label="Product name" required testid="field-name" helper="Shown on the storefront card and product detail page.">
                                    <input
                                        required
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        data-testid="product-form-name"
                                        placeholder="e.g. Reddog Spirit Cap"
                                        className="w-full rounded-xl border border-black/10 px-4 py-3 focus:border-[#E53935] focus:outline-none transition-colors"
                                    />
                                </Field>
                                <Field label="Description" required testid="field-description" helper="2–3 sentences. Mention materials, fit, or sponsorship impact.">
                                    <textarea
                                        required
                                        value={form.description}
                                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                                        rows={3}
                                        data-testid="product-form-description"
                                        placeholder="Describe the product, materials, or impact…"
                                        className="w-full rounded-xl border border-black/10 px-4 py-3 resize-none focus:border-[#E53935] focus:outline-none transition-colors"
                                    />
                                </Field>
                            </Section>

                            {/* Pricing */}
                            <Section title="Pricing" subtitle="What it costs the foundation vs. what supporters pay.">
                                <div className="grid grid-cols-2 gap-4">
                                    <Field label="Cost (what we pay)" testid="field-cost" helper={form.category === "donation" ? "Sponsorships are 100% direct — leave at $0." : "Your wholesale/supplier cost per unit. Drives margin calc."}>
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#4A5568] text-sm">$</span>
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={form.cost}
                                                onChange={(e) => setForm({ ...form, cost: e.target.value })}
                                                data-testid="product-form-cost"
                                                placeholder="0.00"
                                                disabled={form.category === "donation"}
                                                className="w-full rounded-xl border border-black/10 pl-8 pr-4 py-3 focus:border-[#E53935] focus:outline-none transition-colors disabled:bg-[#F4F1EA] disabled:text-[#4A5568]"
                                            />
                                        </div>
                                    </Field>
                                    <Field label="Sale price (what supporter pays)" required testid="field-price" helper="The amount charged at checkout. Net proceeds support approved programs.">
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#4A5568] text-sm">$</span>
                                            <input
                                                required
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={form.price}
                                                onChange={(e) => setForm({ ...form, price: e.target.value })}
                                                data-testid="product-form-price"
                                                placeholder="0.00"
                                                className="w-full rounded-xl border border-black/10 pl-8 pr-4 py-3 focus:border-[#E53935] focus:outline-none transition-colors"
                                            />
                                        </div>
                                    </Field>
                                </div>
                                {priceN > 0 && form.category !== "donation" && (
                                    <div className={`rounded-xl p-3 text-xs ${marginPct >= 50 ? "bg-green-50 text-green-800" : marginPct >= 25 ? "bg-amber-50 text-amber-800" : "bg-red-50 text-red-800"}`} data-testid="product-form-margin-readout">
                                        <strong>Profit per unit:</strong> ${profitPerUnit.toFixed(2)} &nbsp;·&nbsp;
                                        <strong>Margin:</strong> {marginPct.toFixed(1)}% &nbsp;·&nbsp;
                                        <span>Net per sale flows to the Reddog General Support Fund.</span>
                                    </div>
                                )}
                                {form.category === "donation" && (
                                    <div className="bg-[#F2D8D5]/40 border border-[#E53935]/15 rounded-xl p-3 text-xs text-[#1C252E]" data-testid="product-form-sponsor-note">
                                        <strong>Sponsorship item:</strong> 100% of the sale price funds the listed program directly.
                                    </div>
                                )}
                            </Section>

                            {/* Inventory */}
                            <Section title="Inventory" subtitle="Stock count and SKU for fulfillment.">
                                <div className="grid grid-cols-2 gap-4">
                                    <Field label="Stock on hand" required testid="field-stock" helper="Set high (e.g. 9999) for print-on-demand or sponsorship items.">
                                        <input
                                            required
                                            type="number"
                                            min="0"
                                            value={form.stock}
                                            onChange={(e) => setForm({ ...form, stock: e.target.value })}
                                            data-testid="product-form-stock"
                                            placeholder="100"
                                            className="w-full rounded-xl border border-black/10 px-4 py-3 focus:border-[#E53935] focus:outline-none transition-colors"
                                        />
                                    </Field>
                                    <Field label="SKU" testid="field-sku" helper="Internal product code (optional). E.g. RDOG-CAP-001.">
                                        <input
                                            value={form.sku}
                                            onChange={(e) => setForm({ ...form, sku: e.target.value })}
                                            data-testid="product-form-sku"
                                            placeholder="RDOG-XXX-001"
                                            className="w-full rounded-xl border border-black/10 px-4 py-3 font-mono text-sm focus:border-[#E53935] focus:outline-none transition-colors"
                                        />
                                    </Field>
                                </div>
                            </Section>

                            {/* Categorization */}
                            <Section title="Categorization" subtitle="How the product is grouped on the storefront.">
                                <Field label="Category" required testid="field-category" helper="Need a new one? Use 'Manage categories' on the products page.">
                                    <select
                                        value={form.category}
                                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                                        data-testid="product-form-category"
                                        className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white focus:border-[#E53935] focus:outline-none transition-colors"
                                    >
                                        {categories.map((c) => (
                                            <option key={c.id} value={c.slug}>
                                                {c.label}{c.system ? "" : " (custom)"}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                            </Section>

                            {/* Media */}
                            <Section title="Media" subtitle="A clean, well-lit image works best (square or 4:3).">
                                <Field label="Product image" required testid="field-image">
                                    {form.image_url && (
                                        <img src={form.image_url} alt="preview" className="w-full h-40 object-cover rounded-xl mb-3 bg-[#F4F1EA] border border-black/5" />
                                    )}
                                    <div className="flex gap-2">
                                        <input
                                            placeholder="Paste image URL"
                                            value={form.image_url}
                                            onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                                            data-testid="product-form-image"
                                            className="flex-1 rounded-xl border border-black/10 px-4 py-3 focus:border-[#E53935] focus:outline-none transition-colors"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPicker(true)}
                                            data-testid="product-form-pick-library"
                                            className="px-4 py-3 rounded-xl border border-black/10 hover:bg-[#F4F1EA] text-sm font-medium whitespace-nowrap"
                                        >
                                            Pick from library
                                        </button>
                                        <label className="px-4 py-3 rounded-xl border border-black/10 cursor-pointer hover:bg-[#F4F1EA] text-sm font-medium whitespace-nowrap flex items-center gap-2" data-testid="product-form-upload-label">
                                            <Upload className="w-4 h-4" strokeWidth={1.5} />
                                            {uploading ? "Uploading…" : "Upload"}
                                            <input
                                                type="file"
                                                accept="image/png,image/jpeg,image/webp,image/gif"
                                                className="hidden"
                                                data-testid="product-form-upload-input"
                                                onChange={async (e) => {
                                                    const f = e.target.files?.[0];
                                                    if (!f) return;
                                                    setUploading(true);
                                                    try {
                                                        const fd = new FormData();
                                                        fd.append("file", f);
                                                        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                                                        setForm((prev) => ({ ...prev, image_url: data.url }));
                                                        toast.success("Image uploaded");
                                                    } catch (err) {
                                                        toast.error(err.response?.data?.detail || "Upload failed");
                                                    } finally {
                                                        setUploading(false);
                                                        e.target.value = "";
                                                    }
                                                }}
                                            />
                                        </label>
                                    </div>
                                </Field>
                                <Field label="Extra gallery images" testid="field-gallery" helper="Additional angles or lifestyle shots. Shown as a carousel on the product page. The primary image above is always shown first.">
                                    {form.images && form.images.length > 0 && (
                                        <div className="grid grid-cols-4 gap-2 mb-3" data-testid="product-form-gallery-list">
                                            {form.images.map((url, i) => (
                                                <div key={`${url}-${i}`} className="relative group" data-testid={`product-form-gallery-item-${i}`}>
                                                    <img src={url} alt={`gallery ${i + 1}`} className="w-full h-20 object-cover rounded-lg bg-[#F4F1EA] border border-black/5" />
                                                    <button
                                                        type="button"
                                                        onClick={() => setForm((prev) => ({ ...prev, images: prev.images.filter((_, idx) => idx !== i) }))}
                                                        data-testid={`product-form-gallery-remove-${i}`}
                                                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#1C252E] text-white text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity"
                                                        title="Remove image"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <label className="flex items-center gap-2 px-4 py-3 rounded-xl border border-dashed border-black/15 cursor-pointer hover:bg-[#F4F1EA] text-sm font-medium w-fit" data-testid="product-form-gallery-upload-label">
                                        <Upload className="w-4 h-4" strokeWidth={1.5} />
                                        {uploading ? "Uploading…" : "Add gallery images"}
                                        <input
                                            type="file"
                                            multiple
                                            accept="image/png,image/jpeg,image/webp,image/gif"
                                            className="hidden"
                                            data-testid="product-form-gallery-upload-input"
                                            onChange={async (e) => {
                                                const files = Array.from(e.target.files || []);
                                                if (files.length === 0) return;
                                                setUploading(true);
                                                try {
                                                    const uploaded = [];
                                                    for (const f of files) {
                                                        const fd = new FormData();
                                                        fd.append("file", f);
                                                        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                                                        uploaded.push(data.url);
                                                    }
                                                    setForm((prev) => ({ ...prev, images: [...(prev.images || []), ...uploaded] }));
                                                    toast.success(`Uploaded ${uploaded.length} image${uploaded.length > 1 ? "s" : ""}`);
                                                } catch (err) {
                                                    toast.error(err.response?.data?.detail || "Upload failed");
                                                } finally {
                                                    setUploading(false);
                                                    e.target.value = "";
                                                }
                                            }}
                                        />
                                    </label>
                                </Field>
                            </Section>

                            {/* Shipping specs — used for ShipStation rate quotes + label printing */}
                            <Section title="Shipping specs" subtitle="Weight and box dimensions — used for live carrier rate quotes and ShipStation labels.">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                    <Field label="Weight (oz)" testid="field-weight">
                                        <input
                                            type="number"
                                            step="0.1"
                                            min="0.1"
                                            value={form.weight_oz}
                                            onChange={(e) => setForm({ ...form, weight_oz: e.target.value })}
                                            data-testid="product-form-weight-oz"
                                            className="w-full rounded-xl border border-black/10 px-4 py-3 focus:border-[#E53935] focus:outline-none transition-colors"
                                        />
                                    </Field>
                                    <Field label="Length (in)" testid="field-length">
                                        <input
                                            type="number"
                                            step="0.1"
                                            min="0.1"
                                            value={form.length_in}
                                            onChange={(e) => setForm({ ...form, length_in: e.target.value })}
                                            data-testid="product-form-length-in"
                                            className="w-full rounded-xl border border-black/10 px-4 py-3 focus:border-[#E53935] focus:outline-none transition-colors"
                                        />
                                    </Field>
                                    <Field label="Width (in)" testid="field-width">
                                        <input
                                            type="number"
                                            step="0.1"
                                            min="0.1"
                                            value={form.width_in}
                                            onChange={(e) => setForm({ ...form, width_in: e.target.value })}
                                            data-testid="product-form-width-in"
                                            className="w-full rounded-xl border border-black/10 px-4 py-3 focus:border-[#E53935] focus:outline-none transition-colors"
                                        />
                                    </Field>
                                    <Field label="Height (in)" testid="field-height">
                                        <input
                                            type="number"
                                            step="0.1"
                                            min="0.1"
                                            value={form.height_in}
                                            onChange={(e) => setForm({ ...form, height_in: e.target.value })}
                                            data-testid="product-form-height-in"
                                            className="w-full rounded-xl border border-black/10 px-4 py-3 focus:border-[#E53935] focus:outline-none transition-colors"
                                        />
                                    </Field>
                                </div>
                            </Section>

                            {/* Visibility */}
                            <Section title="Visibility" subtitle="Control where the product appears.">
                                <label className="flex items-start gap-3 p-3 rounded-xl border border-black/10 hover:bg-[#F4F1EA]/50 cursor-pointer" data-testid="product-form-featured-wrap">
                                    <input
                                        type="checkbox"
                                        checked={form.featured}
                                        onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                                        data-testid="product-form-featured"
                                        className="mt-1 w-4 h-4 accent-[#E53935]"
                                    />
                                    <div>
                                        <p className="text-sm font-medium text-[#1C252E]">Featured product</p>
                                        <p className="text-xs text-[#4A5568] mt-0.5">Highlights the product on the homepage spirit-shop section.</p>
                                    </div>
                                </label>
                                <label className="flex items-start gap-3 p-3 rounded-xl border border-black/10 hover:bg-[#F4F1EA]/50 cursor-pointer" data-testid="product-form-active-wrap">
                                    <input
                                        type="checkbox"
                                        checked={form.active}
                                        onChange={(e) => setForm({ ...form, active: e.target.checked })}
                                        data-testid="product-form-active"
                                        className="mt-1 w-4 h-4 accent-[#E53935]"
                                    />
                                    <div>
                                        <p className="text-sm font-medium text-[#1C252E]">Active on storefront</p>
                                        <p className="text-xs text-[#4A5568] mt-0.5">Uncheck to hide the product from public Shop without deleting it. Already-placed orders are unaffected.</p>
                                    </div>
                                </label>
                            </Section>
                        </form>
                        <div className="border-t border-black/5 px-8 py-4 flex items-center justify-end gap-3 bg-[#FDFBF7]">
                            <button type="button" onClick={close} className="px-5 py-2.5 text-sm font-medium text-[#4A5568] hover:text-[#1C252E]" data-testid="product-form-cancel">
                                Cancel
                            </button>
                            <button
                                type="submit"
                                onClick={save}
                                data-testid="product-form-submit"
                                className="bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-7 py-2.5 text-sm font-medium transition-all"
                            >
                                {editing ? "Save changes" : "Create product"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showCats && (
                <CategoriesModal
                    categories={categories}
                    onClose={() => setShowCats(false)}
                    onChanged={() => { loadCategories(); loadProducts(); }}
                />
            )}
            <ImagePicker
                open={showPicker}
                onClose={() => setShowPicker(false)}
                onPick={(img) => setForm((prev) => ({ ...prev, image_url: img.url }))}
                category="products"
            />
        </div>
    );
};

// ============== Categories Management Modal ==============
const CategoriesModal = ({ categories, onClose, onChanged }) => {
    const [newSlug, setNewSlug] = useState("");
    const [newLabel, setNewLabel] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editLabel, setEditLabel] = useState("");
    const [editSlug, setEditSlug] = useState("");
    const [busy, setBusy] = useState(false);

    const create = async (e) => {
        e.preventDefault();
        if (!newSlug.trim() || !newLabel.trim()) return;
        setBusy(true);
        try {
            await api.post("/admin/categories", { slug: newSlug.trim(), label: newLabel.trim(), sort_order: 100 });
            toast.success("Category created");
            setNewSlug("");
            setNewLabel("");
            onChanged();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to create");
        } finally {
            setBusy(false);
        }
    };

    const startEdit = (c) => {
        setEditingId(c.id);
        setEditLabel(c.label);
        setEditSlug(c.slug);
    };

    const saveEdit = async (c) => {
        setBusy(true);
        try {
            await api.patch(`/admin/categories/${c.id}`, {
                slug: c.system ? c.slug : editSlug.trim(),
                label: editLabel.trim(),
                sort_order: c.sort_order || 100,
            });
            toast.success("Category updated");
            setEditingId(null);
            onChanged();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to update");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (c) => {
        if (!window.confirm(`Delete category "${c.label}"?`)) return;
        setBusy(true);
        try {
            await api.delete(`/admin/categories/${c.id}`);
            toast.success("Category deleted");
            onChanged();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-categories-modal">
            <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="px-8 py-5 border-b border-black/5 flex justify-between items-center">
                    <div>
                        <h3 className="font-heading text-2xl text-[#1C252E] font-medium">Product categories</h3>
                        <p className="text-xs text-[#4A5568] mt-1">Storefront filters and product grouping. System categories can be renamed but not deleted.</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-[#F4F1EA] rounded-full" aria-label="Close"><X className="w-4 h-4" /></button>
                </div>

                <div className="overflow-y-auto px-8 py-6 flex-1 space-y-5">
                    <ul className="divide-y divide-black/5" data-testid="admin-categories-list">
                        {categories.map((c) => (
                            <li key={c.id} className="py-3 flex items-center gap-3" data-testid={`admin-category-row-${c.slug}`}>
                                {editingId === c.id ? (
                                    <>
                                        <input
                                            value={editLabel}
                                            onChange={(e) => setEditLabel(e.target.value)}
                                            placeholder="Label"
                                            className="flex-1 rounded-lg border border-black/10 px-3 py-2 text-sm"
                                            data-testid={`admin-category-edit-label-${c.slug}`}
                                        />
                                        {!c.system && (
                                            <input
                                                value={editSlug}
                                                onChange={(e) => setEditSlug(e.target.value)}
                                                placeholder="slug"
                                                className="w-32 rounded-lg border border-black/10 px-3 py-2 text-xs font-mono"
                                                data-testid={`admin-category-edit-slug-${c.slug}`}
                                            />
                                        )}
                                        <button
                                            onClick={() => saveEdit(c)}
                                            disabled={busy}
                                            data-testid={`admin-category-save-${c.slug}`}
                                            className="text-xs px-3 py-2 rounded-full bg-[#E53935] text-white hover:bg-[#D32F2F]"
                                        >
                                            Save
                                        </button>
                                        <button
                                            onClick={() => setEditingId(null)}
                                            className="text-xs px-3 py-2 rounded-full border border-black/10 hover:bg-[#F4F1EA]"
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-[#1C252E] flex items-center gap-2">
                                                {c.label}
                                                {c.system && <span className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] bg-[#F4F1EA] px-1.5 py-0.5 rounded">System</span>}
                                            </p>
                                            <p className="text-[11px] text-[#4A5568] font-mono">{c.slug} · {c.product_count ?? 0} product(s)</p>
                                        </div>
                                        <button
                                            onClick={() => startEdit(c)}
                                            data-testid={`admin-category-edit-${c.slug}`}
                                            className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#4A5568]"
                                            aria-label="Edit"
                                        >
                                            <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                                        </button>
                                        {!c.system && (
                                            <button
                                                onClick={() => remove(c)}
                                                data-testid={`admin-category-delete-${c.slug}`}
                                                className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#E53935]"
                                                aria-label="Delete"
                                            >
                                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                            </button>
                                        )}
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>

                    <form onSubmit={create} className="border-t border-black/5 pt-5 space-y-3" data-testid="admin-category-create-form">
                        <h4 className="text-sm font-semibold text-[#1C252E]">Add new category</h4>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[11px] uppercase tracking-wider font-bold text-[#4A5568] block mb-1">Display label</label>
                                <input
                                    value={newLabel}
                                    onChange={(e) => setNewLabel(e.target.value)}
                                    placeholder="e.g. Stickers"
                                    className="w-full rounded-lg border border-black/10 px-3 py-2 text-sm"
                                    data-testid="admin-category-new-label"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-wider font-bold text-[#4A5568] block mb-1">URL slug</label>
                                <input
                                    value={newSlug}
                                    onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/\s+/g, "-"))}
                                    placeholder="e.g. stickers"
                                    className="w-full rounded-lg border border-black/10 px-3 py-2 text-sm font-mono"
                                    data-testid="admin-category-new-slug"
                                />
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={busy || !newSlug.trim() || !newLabel.trim()}
                            data-testid="admin-category-create-btn"
                            className="bg-[#E53935] text-white hover:bg-[#D32F2F] disabled:opacity-50 rounded-full px-5 py-2 text-sm font-medium"
                        >
                            Add category
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

// ============================================================
// PaymentRefCell — small inline widget shown in Admin → Orders that
// surfaces the payment gateway + its transaction reference (Stripe
// payment intent id, PayPal capture id, or "mock"). Clicking the ref
// opens the transaction in Stripe / PayPal dashboards for reconciliation.
// Also lets an admin copy the ID to the clipboard with one click.
// ============================================================
const PaymentRefCell = ({ order }) => {
    const method = (order.payment_method || "").toLowerCase();
    const ref = order.payment_reference || "";
    const copyRef = async (e) => {
        e.stopPropagation();
        e.preventDefault();
        try {
            await navigator.clipboard.writeText(ref);
            toast.success("Payment ID copied");
        } catch { toast.error("Copy failed"); }
    };
    // No ref yet — order was created but payment hasn't confirmed.
    if (!ref) {
        return (
            <div className="text-xs">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider text-[10px] ${method === "stripe" ? "bg-indigo-100 text-indigo-700" : method === "paypal" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                    {method || "—"}
                </span>
                <p className="text-[10px] text-slate-500 mt-0.5 italic">Not yet paid</p>
            </div>
        );
    }
    // Build the "open in dashboard" URL when we recognize the gateway.
    let dashUrl = null;
    if (method === "stripe" && ref.startsWith("pi_")) dashUrl = `https://dashboard.stripe.com/payments/${ref}`;
    if (method === "paypal") dashUrl = `https://www.paypal.com/activity/payment/${ref}`;
    const shortRef = ref.length > 20 ? `${ref.slice(0, 10)}…${ref.slice(-6)}` : ref;
    return (
        <div className="text-xs" data-testid={`admin-order-payment-${order.id}`}>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider text-[10px] ${method === "stripe" ? "bg-indigo-100 text-indigo-700" : method === "paypal" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                {method}
            </span>
            <div className="flex items-center gap-1 mt-1">
                {dashUrl ? (
                    <a
                        href={dashUrl}
                        target="_blank"
                        rel="noreferrer"
                        title={`Open ${method} transaction ${ref}`}
                        className="font-mono text-[10px] text-[#1C252E] hover:text-[#E53935] hover:underline"
                    >
                        {shortRef}
                    </a>
                ) : (
                    <span className="font-mono text-[10px] text-[#1C252E]" title={ref}>{shortRef}</span>
                )}
                <button
                    onClick={copyRef}
                    title="Copy payment ID"
                    data-testid={`admin-order-payment-copy-${order.id}`}
                    className="text-slate-400 hover:text-[#E53935] transition-colors"
                >
                    <Copy className="w-3 h-3" strokeWidth={2} />
                </button>
            </div>
        </div>
    );
};

const OrdersPanel = () => {
    const [orders, setOrders] = useState([]);
    const [ordersError, setOrdersError] = useState("");
    const [plMap, setPlMap] = useState({});  // order_id -> { revenue, cogs, net_to_gsf }
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedIds, setSelectedIds] = useState(() => new Set()); // for bulk print
    // "Abandoned checkouts" — customer clicked Pay on Stripe/PayPal but never
    // finished. Sweeper auto-cancels them after 30 min. Hidden by default so
    // they don't clutter the active list. NOTE: this is Stripe/PayPal only —
    // COD-pending orders are ALWAYS shown in the active view because someone
    // needs to physically fulfill them (that's the whole point of COD).
    const [showAbandoned, setShowAbandoned] = useState(false);
    // Archive view = delivered orders. Toggled OFF by default so admins see
    // only actionable work; delivered orders slide out of the way once done.
    const [archiveView, setArchiveView] = useState(false);
    // Filter controls
    const [search, setSearch] = useState("");
    const [fStatus, setFStatus] = useState("all");     // fulfillment status
    const [fPayment, setFPayment] = useState("all");   // payment status
    const [fDate, setFDate] = useState("all");         // last 7d / 30d / all

    const load = () => {
        api.get("/orders")
            .then((r) => { setOrders(r.data); setOrdersError(""); setSelectedOrder(prev => prev ? r.data.find(order => order.id === prev.id) || null : null); })
            .catch((err) => { setOrdersError(formatApiError(err, "Couldn't load orders")); });
        api.get("/admin/orders-pl").then((r) => {
            const m = {};
            (r.data.orders || []).forEach((p) => { m[p.order_id] = p; });
            setPlMap(m);
        }).catch(() => {});
    };
    useEffect(() => {
        load();
        const timer = setInterval(() => { if (!document.hidden) load(); }, 20000);
        window.addEventListener("focus", load);
        return () => { clearInterval(timer); window.removeEventListener("focus", load); };
    }, []);

    const removeOrder = async (id, orderNumber, onDone) => {
        if (!window.confirm(`Permanently delete order ${orderNumber}? This can only be done on cancelled orders and cannot be undone.`)) return;
        try {
            await api.delete(`/orders/${id}`);
            toast.success(`Deleted ${orderNumber}`);
            if (onDone) onDone();
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete failed");
        }
    };

    const bulkDelete = async () => {
        // Only cancelled orders in the selection are eligible; anything else
        // is filtered client-side so the confirm dialog shows the true count.
        const eligibleIds = [...selectedIds].filter((id) => {
            const o = orders.find((x) => x.id === id);
            return o && o.fulfillment_status === "cancelled";
        });
        if (eligibleIds.length === 0) {
            toast.error("Select at least one cancelled order to bulk delete.");
            return;
        }
        if (!window.confirm(`Permanently delete ${eligibleIds.length} cancelled order${eligibleIds.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
        try {
            const { data } = await api.post("/orders/bulk-delete", { order_ids: eligibleIds });
            const del = data?.deleted_count || 0;
            const skip = data?.skipped_count || 0;
            if (del > 0) toast.success(`Deleted ${del} order${del === 1 ? "" : "s"}${skip > 0 ? ` · ${skip} skipped` : ""}`);
            else toast.info("Nothing deleted — every selection was skipped.");
            setSelectedIds(new Set());
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Bulk delete failed");
        }
    };

    const toggleSelect = (id) => setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const bulkPrint = () => {
        if (!selectedIds.size) return;
        const ids = [...selectedIds].join(",");
        const token = localStorage.getItem("token");
        // Use fetch to include auth header, then open the resulting blob in a new tab
        fetch(`${API}/books/orders/bulk-print.pdf?ids=${encodeURIComponent(ids)}`, {
            headers: { Authorization: `Bearer ${token}` },
        }).then(async (r) => {
            if (!r.ok) throw new Error(await r.text());
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank");
            toast.success(`Opened ${selectedIds.size} packing slip${selectedIds.size === 1 ? "" : "s"}`);
        }).catch((e) => toast.error(`Bulk print failed: ${e.message || e}`));
    };

    // ── Two-view model ──────────────────────────────────────────────
    // ACTIVE (default): actionable work — new / processing / shipped.
    //   • COD-pending is included (someone must fulfill it).
    //   • Cancelled + payment_status "failed" / "refunded" are hidden.
    //   • Abandoned Stripe/PayPal checkouts (pending, non-COD) hidden
    //     behind a toggle since the sweeper cancels them in 30 min.
    // ARCHIVE (toggle): only fulfillment_status = delivered.
    //
    // The status filter dropdown OVERRIDES the archive/active choice
    // when set to a specific value — that lets admins surface
    // cancelled / delivered / etc. explicitly.
    //
    // Sort within active: new → processing → shipped, then newest first.
    // ────────────────────────────────────────────────────────────────
    const now = Date.now();
    const dayCutoff = fDate === "7d" ? now - 7 * 86400000
                     : fDate === "30d" ? now - 30 * 86400000
                     : fDate === "90d" ? now - 90 * 86400000
                     : null;
    const q = search.trim().toLowerCase();
    const isAbandoned = (o) => o.payment_status === "pending" && ["stripe", "paypal"].includes((o.payment_method || "").toLowerCase());

    const passesActiveView = (o) => {
        // Explicit status filter wins — always show that exact status.
        if (fStatus !== "all") return o.fulfillment_status === fStatus;
        if (archiveView) return o.fulfillment_status === "delivered";
        // Default active view: new/processing/shipped only.
        if (!["new", "processing", "shipped"].includes(o.fulfillment_status)) return false;
        // Hide abandoned checkouts (Stripe/PayPal pending) unless toggled on.
        if (!showAbandoned && fPayment !== "pending" && isAbandoned(o)) return false;
        // Hide payments that will never succeed.
        if (o.payment_status === "failed" || o.payment_status === "refunded") return false;
        return true;
    };

    const visibleOrders = orders.filter((o) => {
        if (!passesActiveView(o)) return false;
        if (fPayment !== "all" && o.payment_status !== fPayment) return false;
        if (dayCutoff && o.created_at && new Date(o.created_at).getTime() < dayCutoff) return false;
        if (q) {
            const hay = `${o.order_number || ""} ${o.customer_name || ""} ${o.customer_email || ""} ${o.payment_reference || ""}`.toLowerCase();
            if (!hay.includes(q)) return false;
        }
        return true;
    });

    // Sort: new → processing → shipped in active view; delivered by date desc in archive.
    const STATUS_RANK = { new: 0, processing: 1, shipped: 2, delivered: 3, cancelled: 4 };
    visibleOrders.sort((a, b) => {
        const ra = STATUS_RANK[a.fulfillment_status] ?? 5;
        const rb = STATUS_RANK[b.fulfillment_status] ?? 5;
        if (ra !== rb) return ra - rb;
        const ta = new Date(a.created_at || 0).getTime();
        const tb = new Date(b.created_at || 0).getTime();
        return tb - ta;
    });

    const abandonedCount = orders.filter((o) => isAbandoned(o) && ["new", "processing", "shipped"].includes(o.fulfillment_status)).length;
    const archivedCount = orders.filter((o) => o.fulfillment_status === "delivered").length;
    const anyFilterActive = q || fStatus !== "all" || fPayment !== "all" || fDate !== "all";
    const clearFilters = () => { setSearch(""); setFStatus("all"); setFPayment("all"); setFDate("all"); };

    const setStatus = async (id, status) => {
        try {
            if (status === "cancelled") {
                if (!window.confirm("Cancel this order? This will void the ledger entry, restore stock, cancel any ShipStation shipment, and roll back coupon usage.")) return;
                const { data } = await api.post(`/orders/${id}/cancel`);
                if (data?.already_cancelled) {
                    toast.info("Already cancelled");
                } else {
                    toast.success("Order cancelled · JE voided · stock restored");
                }
            } else {
                await api.put(`/orders/${id}/status`, { fulfillment_status: status });
                toast.success("Status updated");
            }
            // Optimistically update the modal's selected order + refresh list
            setSelectedOrder((prev) => prev && prev.id === id ? { ...prev, fulfillment_status: status } : prev);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Update failed");
        }
    };

    // Rollup of the currently-visible list (excludes pending checkouts + cancels via PL API)
    const totals = visibleOrders.reduce((acc, o) => {
        const p = plMap[o.id];
        if (!p) return acc;
        acc.revenue += p.revenue;
        acc.cogs += p.cogs;
        acc.net_to_gsf += p.net_to_gsf;
        return acc;
    }, { revenue: 0, cogs: 0, net_to_gsf: 0 });

    return (
        <div data-testid="admin-orders-panel-wrap">
            <OfflinePaymentsPanel onConfirmed={load} />
            {ordersError && <div data-testid="admin-orders-load-error" role="alert" className="mb-4 text-sm text-red-700"><p>{ordersError}</p><a href="/account" data-testid="admin-orders-account-link" className="underline">Account settings</a></div>}
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] font-medium">
                        Orders{archiveView ? " · archive" : ""}
                    </h2>
                    <p className="text-sm text-[#4A5568] mt-1">
                        {visibleOrders.length} shown
                        {!archiveView && archivedCount > 0 ? ` · ${archivedCount} delivered in archive` : ""}
                        {!archiveView && abandonedCount > 0 && !showAbandoned ? ` · ${abandonedCount} abandoned checkout${abandonedCount === 1 ? "" : "s"} hidden` : ""}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {selectedIds.size > 0 && (
                        <button
                            onClick={bulkPrint}
                            data-testid="admin-orders-bulk-print"
                            className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#B91C1C] rounded-full px-4 py-2 text-xs font-medium transition-all"
                            title="Generate a single PDF with one packing slip per selected order"
                        >
                            <FileText className="w-4 h-4" strokeWidth={1.5} />
                            Print {selectedIds.size} packing slip{selectedIds.size === 1 ? "" : "s"}
                        </button>
                    )}
                    {(() => {
                        const cancelledSelected = [...selectedIds].filter((id) => {
                            const o = orders.find((x) => x.id === id);
                            return o && o.fulfillment_status === "cancelled";
                        }).length;
                        if (cancelledSelected === 0) return null;
                        return (
                            <button
                                onClick={bulkDelete}
                                data-testid="admin-orders-bulk-delete"
                                className="inline-flex items-center gap-2 bg-white text-red-700 border border-red-300 hover:bg-red-50 rounded-full px-4 py-2 text-xs font-medium transition-all"
                                title="Permanently delete every cancelled order in the current selection. Paid and pending orders are skipped automatically."
                            >
                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                Delete {cancelledSelected} cancelled
                            </button>
                        );
                    })()}
                    {abandonedCount > 0 && !archiveView && (
                        <button
                            onClick={() => setShowAbandoned((v) => !v)}
                            data-testid="admin-orders-toggle-abandoned"
                            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium border transition-all ${showAbandoned ? "bg-amber-500 text-white border-amber-500" : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50"}`}
                            title="Abandoned checkouts (Stripe / PayPal pending) auto-cancel after 30 min. Hidden by default."
                        >
                            {showAbandoned ? "Hide" : "Show"} {abandonedCount} abandoned checkout{abandonedCount === 1 ? "" : "s"}
                        </button>
                    )}
                    <button
                        onClick={() => setArchiveView((v) => !v)}
                        data-testid="admin-orders-toggle-archive"
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium border transition-all ${archiveView ? "bg-[#1C252E] text-white border-[#1C252E]" : "bg-white text-[#4A5568] border-black/15 hover:bg-[#F4F1EA]"}`}
                        title="Archive shows delivered orders. Uncheck to return to active work."
                    >
                        <Archive className="w-4 h-4" strokeWidth={1.5} />
                        {archiveView ? "Active orders" : `Archive (${archivedCount})`}
                    </button>
                    <a
                        href={`${API}/orders/export.csv`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="admin-export-orders-btn"
                        className="inline-flex items-center gap-2 bg-[#1C252E] text-white hover:bg-black rounded-full px-5 py-2 text-sm font-medium transition-all"
                    >
                        <Download className="w-4 h-4" strokeWidth={1.5} />
                        Export CSV
                    </a>
                </div>
            </div>

            {/* Filter bar */}
            <div className="mb-4 flex flex-wrap items-center gap-2 bg-white border border-black/5 rounded-2xl px-3 py-2" data-testid="admin-orders-filter-bar">
                <div className="relative flex-1 min-w-[220px]">
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search order #, customer, email, payment ref…"
                        data-testid="admin-orders-search"
                        className="w-full pl-9 pr-3 py-2 rounded-full border border-black/10 text-sm bg-[#F4F1EA]/50 focus:outline-none focus:ring-2 focus:ring-[#E53935]/30 focus:border-[#E53935]"
                    />
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5568]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                </div>
                <select value={fPayment} onChange={(e) => setFPayment(e.target.value)} data-testid="admin-orders-filter-payment" className="px-3 py-2 text-xs rounded-full border border-black/10 bg-white">
                    <option value="all">All payment</option>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                    <option value="refunded">Refunded</option>
                    <option value="failed">Failed</option>
                </select>
                <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="admin-orders-filter-status" className="px-3 py-2 text-xs rounded-full border border-black/10 bg-white">
                    <option value="all">All status</option>
                    <option value="new">New</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                </select>
                <select value={fDate} onChange={(e) => setFDate(e.target.value)} data-testid="admin-orders-filter-date" className="px-3 py-2 text-xs rounded-full border border-black/10 bg-white">
                    <option value="all">All time</option>
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="90d">Last 90 days</option>
                </select>
                {anyFilterActive && (
                    <button onClick={clearFilters} data-testid="admin-orders-clear-filters" className="px-3 py-2 text-xs rounded-full border border-red-300 text-red-700 hover:bg-red-50">
                        Clear
                    </button>
                )}
            </div>

            {/* P&L rollup mini-bar */}
            {visibleOrders.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="admin-orders-pl-rollup">
                    <div className="bg-white border border-black/5 rounded-xl px-4 py-3">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">Revenue</p>
                        <p className="font-heading text-xl text-[#1C252E] font-medium">${totals.revenue.toFixed(2)}</p>
                    </div>
                    <div className="bg-white border border-black/5 rounded-xl px-4 py-3">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Cost recovered</p>
                        <p className="font-heading text-xl text-[#1C252E] font-medium">${totals.cogs.toFixed(2)}</p>
                    </div>
                    <div className="bg-[#F2D8D5]/50 border border-[#E53935]/20 rounded-xl px-4 py-3">
                        <p className="text-[10px] uppercase tracking-wider font-bold text-[#E53935]">Net to GSF</p>
                        <p className="font-heading text-xl text-[#1C252E] font-medium">${totals.net_to_gsf.toFixed(2)}</p>
                    </div>
                </div>
            )}

            {/* Mobile (<sm): stacked cards */}
            <div className="sm:hidden space-y-3" data-testid="admin-orders-panel-mobile">
                {visibleOrders.length === 0 ? (
                    <p className="text-center text-[#4A5568] py-8" data-testid="admin-orders-empty-mobile">No orders yet</p>
                ) : visibleOrders.map((o) => {
                    const pl = plMap[o.id] || { cogs: 0, net_to_gsf: 0 };
                    return (
                        <div
                            key={o.id}
                            className="bg-white border border-black/5 rounded-2xl p-4 cursor-pointer hover:border-[#E53935]/30 transition-colors"
                            data-testid={`admin-order-card-${o.id}`}
                            onClick={(e) => {
                                if (e.target.closest("select, a, button")) return;
                                setSelectedOrder(o);
                            }}
                        >
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div className="min-w-0">
                                    <p className="font-mono text-[11px] uppercase tracking-wider text-[#4A5568]">{o.order_number}</p>
                                    <p className="font-medium text-[#1C252E] truncate">{o.customer_name}</p>
                                    <p className="text-xs text-[#4A5568] truncate">{o.customer_email}</p>
                                </div>
                                <p className="font-heading text-xl text-[#1C252E] font-medium whitespace-nowrap">${o.total.toFixed(2)}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs mb-3 pb-3 border-b border-black/5">
                                <div>
                                    <p className="text-[10px] uppercase tracking-wider text-[#4A5568]">Cost</p>
                                    <p className="font-mono text-amber-700">${pl.cogs.toFixed(2)}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] uppercase tracking-wider text-[#E53935]">→ GSF</p>
                                    <p className="font-mono font-bold text-[#E53935]">${pl.net_to_gsf.toFixed(2)}</p>
                                </div>
                            </div>
                            <div className="mb-3 pb-3 border-b border-black/5">
                                <p className="text-[10px] uppercase tracking-wider text-[#4A5568] mb-1">Payment</p>
                                <PaymentRefCell order={o} />
                            </div>
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <select
                                    value={o.fulfillment_status}
                                    onChange={(e) => setStatus(o.id, e.target.value)}
                                    className="rounded-full border border-black/10 px-3 py-1 text-xs bg-white"
                                >
                                    <option value="new">New</option>
                                    <option value="processing">Processing</option>
                                    <option value="shipped">Shipped</option>
                                    <option value="delivered">Delivered</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                                <span className="text-xs text-[#4A5568]">{o.items.length} item{o.items.length === 1 ? "" : "s"}</span>
                                <a href={`${API}/books/invoice/${o.id}.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-[#E53935] hover:underline">
                                    <FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> PDF
                                </a>
                                {o.fulfillment_status === "cancelled" && (
                                    <button
                                        onClick={() => removeOrder(o.id, o.order_number)}
                                        title="Permanently delete this cancelled order"
                                        className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-red-600"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Desktop (≥sm): traditional table */}
            <div className="hidden sm:block bg-white border border-black/5 rounded-2xl overflow-x-auto" data-testid="admin-orders-panel">
                <table className="w-full text-sm">
                    <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                        <tr>
                            <th className="px-3 py-3 w-8">
                                <input
                                    type="checkbox"
                                    checked={visibleOrders.length > 0 && visibleOrders.every((o) => selectedIds.has(o.id))}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        const allSelected = visibleOrders.every((o) => selectedIds.has(o.id));
                                        setSelectedIds(allSelected ? new Set() : new Set(visibleOrders.map((o) => o.id)));
                                    }}
                                    data-testid="admin-orders-select-all"
                                    className="w-4 h-4 accent-[#E53935]"
                                    title="Select all visible orders"
                                />
                            </th>
                            <th className="text-left px-4 py-3 font-medium">Order #</th>
                            <th className="text-left px-4 py-3 font-medium">Customer</th>
                            <th className="text-left px-4 py-3 font-medium">Items</th>
                            <th className="text-right px-4 py-3 font-medium">Total</th>
                            <th className="text-right px-4 py-3 font-medium" title="Cost of goods recovered for restock">Cost</th>
                            <th className="text-right px-4 py-3 font-medium" title="Flows into the Reddog General Support Fund">→ GSF</th>
                            <th className="text-left px-4 py-3 font-medium" title="Payment gateway + transaction reference">Payment</th>
                            <th className="text-left px-4 py-3 font-medium">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-black/5">
                        {visibleOrders.length === 0 && (
                            <tr><td colSpan="9" className="px-4 py-8 text-center text-[#4A5568]" data-testid="admin-orders-empty">No orders yet</td></tr>
                        )}
                        {visibleOrders.map((o) => {
                            const pl = plMap[o.id] || { cogs: 0, net_to_gsf: 0 };
                            return (
                            <tr
                                key={o.id}
                                data-testid={`admin-order-row-${o.id}`}
                                onClick={(e) => {
                                    // Ignore clicks on the control widgets in the last column.
                                    if (e.target.closest("select, a, button, input")) return;
                                    setSelectedOrder(o);
                                }}
                                className={`hover:bg-[#F4F1EA]/40 cursor-pointer transition-colors ${selectedIds.has(o.id) ? "bg-[#F2D8D5]/30" : ""}`}
                            >
                                <td className="px-3 py-3 w-8">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.has(o.id)}
                                        onChange={() => toggleSelect(o.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        data-testid={`admin-order-select-${o.id}`}
                                        className="w-4 h-4 accent-[#E53935]"
                                    />
                                </td>
                                <td className="px-4 py-3 font-mono text-xs text-[#1C252E]">{o.order_number}</td>
                                <td className="px-4 py-3">
                                    <p className="font-medium text-[#1C252E]">{o.customer_name}</p>
                                    <p className="text-xs text-[#4A5568]">{o.customer_email}</p>
                                </td>
                                <td className="px-4 py-3 text-[#4A5568]">{o.items.length} item(s)</td>
                                <td className="px-4 py-3 text-right font-mono font-medium text-[#1C252E]">${o.total.toFixed(2)}</td>
                                <td className="px-4 py-3 text-right font-mono text-xs text-amber-700">${pl.cogs.toFixed(2)}</td>
                                <td className="px-4 py-3 text-right font-mono text-xs text-[#E53935] font-bold">${pl.net_to_gsf.toFixed(2)}</td>
                                <td className="px-4 py-3">
                                    <PaymentRefCell order={o} />
                                </td>
                                <td className="px-4 py-3">
                                    <select
                                        value={o.fulfillment_status}
                                        onChange={(e) => setStatus(o.id, e.target.value)}
                                        data-testid={`admin-order-status-${o.id}`}
                                        className="rounded-full border border-black/10 px-3 py-1 text-xs bg-white"
                                    >
                                        <option value="new">New</option>
                                        <option value="processing">Processing</option>
                                        <option value="shipped">Shipped</option>
                                        <option value="delivered">Delivered</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                    <a
                                        href={`${API}/books/invoice/${o.id}.pdf`}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`admin-order-invoice-${o.id}`}
                                        className="inline-flex items-center gap-1 text-xs font-medium text-[#E53935] hover:underline ml-2"
                                        title="Download invoice PDF"
                                    >
                                        <FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> PDF
                                    </a>
                                    {o.fulfillment_status === "cancelled" && (
                                        <button
                                            onClick={() => removeOrder(o.id, o.order_number)}
                                            data-testid={`admin-order-delete-${o.id}`}
                                            title="Permanently delete this cancelled order"
                                            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-red-600 ml-2"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Delete
                                        </button>
                                    )}
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <OrderDetailModal
                order={selectedOrder}
                pl={selectedOrder ? plMap[selectedOrder.id] : null}
                onClose={() => setSelectedOrder(null)}
                onSetStatus={setStatus}
                onDelete={removeOrder}
                onRefresh={load}
            />
        </div>
    );
};

// "Verified" badge — shows tamper-proof audit state for PayPal/Stripe donations.
//   ✅ Verified         → both capture/intent AND webhook confirmed it (strongest)
//   ⏳ Webhook pending  → capture/intent confirmed, but the signed webhook hasn't arrived yet
//   🔔 Capture pending  → webhook arrived first; on-page capture still owed
// Works for both PayPal ("capture" + "webhook") and Stripe ("intent" + "webhook")
// since `_attribute_capture` / `_attribute_stripe` use the same channel names.
const PaypalVerifiedBadge = ({ donation, testid }) => {
    const channels = Array.isArray(donation.confirmed_by) ? donation.confirmed_by : [];
    const hasCapture = channels.includes("capture") || channels.includes("reconcile") || channels.includes("intent");
    const hasWebhook = channels.includes("webhook");
    if (donation.verified || (hasCapture && hasWebhook)) {
        return (
            <span
                data-testid={testid}
                title={`Confirmed by ${channels.join(" + ")}`}
                className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 text-[10px] font-bold uppercase tracking-wider"
            >
                <ShieldCheck className="w-3 h-3" strokeWidth={2.5} /> Verified
            </span>
        );
    }
    if (hasCapture && !hasWebhook) {
        return (
            <span
                data-testid={testid}
                title="Capture handler confirmed this donation. Waiting for PayPal's signed webhook to fully verify."
                className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 text-[10px] font-bold uppercase tracking-wider"
            >
                <Clock className="w-3 h-3" strokeWidth={2.5} /> Webhook pending
            </span>
        );
    }
    if (hasWebhook && !hasCapture) {
        return (
            <span
                data-testid={testid}
                title="PayPal's webhook confirmed this donation. The capture handler will reconcile shortly."
                className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 text-[10px] font-bold uppercase tracking-wider"
            >
                <Clock className="w-3 h-3" strokeWidth={2.5} /> Capture pending
            </span>
        );
    }
    return null;
};


const DonationsPanel = () => {
    const [items, setItems] = useState([]);
    const [campaigns, setCampaigns] = useState([]);

    const reload = () => api.get("/donations").then((r) => setItems(r.data)).catch(() => {});
    useEffect(() => {
        reload();
        api.get("/campaigns").then((r) => setCampaigns(r.data || [])).catch(() => {});
    }, []);

    // Inline editor for donor name / email / anonymity. `editing` is null
    // when closed; otherwise it's the donation row being edited.
    const [editing, setEditing] = useState(null);
    const [manualCheckOpen, setManualCheckOpen] = useState(false);

    const campaignName = (id) => {
        if (!id) return null;
        const c = campaigns.find((x) => x.id === id);
        return c ? c.name : "Unknown campaign";
    };
    const campaignRestricted = (id) => {
        const c = campaigns.find((x) => x.id === id);
        return !!(c && c.restricted_fund);
    };

    const cancelDonation = async (id) => {
        if (!window.confirm("Cancel this recurring donation?")) return;
        try {
            await api.put(`/donations/${id}/cancel`);
            toast.success("Donation cancelled");
            reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Cancel failed");
        }
    };

    const deleteDonation = async (d) => {
        if (!window.confirm(`Permanently delete this donation?\n\n${d.donor_name || "Anonymous"} · $${d.amount.toFixed(2)}\n\nThis also voids the journal entry. This action cannot be undone.`)) return;
        try {
            const { data } = await api.delete(`/admin/donations/${d.id}`);
            toast.success(`Donation removed (${data.voided_journal_entries} JE voided)`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const resyncFromStripe = async (d) => {
        if (!d.stripe_payment_intent_id) {
            toast.error("This donation has no Stripe PaymentIntent to re-sync");
            return;
        }
        try {
            const { data } = await api.post(`/stripe/admin/resync/${d.stripe_payment_intent_id}`);
            if (data.ok) {
                toast.success("Re-synced from Stripe · marked verified");
            } else {
                toast.error(data.detail || `Status: ${data.status}`);
            }
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Re-sync failed");
        }
    };

    const repostJE = async (d) => {
        if (!window.confirm("Void the existing journal entry and post a fresh one? This is safe — it just rebuilds the GL entry using current campaign/restricted settings.")) return;
        try {
            await api.post(`/admin/financials/repost-donation/${d.id}`);
            toast.success("Journal entry re-posted");
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Re-post failed");
        }
    };

    const changeCampaign = async (d) => {
        const current = d.campaign_id || "";
        const options = campaigns.map((c, i) => `  ${i + 1}. ${c.name}${c.restricted_fund ? " (restricted)" : ""}`).join("\n");
        const choice = window.prompt(`Move this donation to which campaign?\n\n  0. General Fund (no specific campaign)\n${options}\n\nEnter a number:`, "0");
        if (choice === null) return;
        const idx = parseInt(choice, 10);
        const newCampaignId = idx === 0 ? null : (campaigns[idx - 1] || {}).id;
        if (idx > 0 && !newCampaignId) { toast.error("Invalid selection"); return; }
        if (newCampaignId === current || (idx === 0 && !current)) { toast("No change"); return; }
        try {
            await api.put(`/admin/donations/${d.id}/campaign`, { campaign_id: newCampaignId });
            toast.success("Campaign updated · journal entry re-posted");
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
    };

    return (
        <div data-testid="admin-donations-panel-wrap">
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
                <p className="text-sm text-[#4A5568]">{items.length} donations · {items.filter(d => d.frequency === "monthly" && d.status !== "cancelled").length} active monthly</p>
                <div className="flex gap-2 flex-wrap">
                    <button
                        onClick={async () => {
                            if (!window.confirm("Backfill missing journal entries for every paid donation?")) return;
                            try {
                                const { data } = await api.post("/admin/financials/reconcile");
                                toast.success(`Reconciled: ${data.posted} JE(s) posted, ${data.skipped_already_posted} already current`);
                                reload();
                            } catch (e) {
                                toast.error(e?.response?.data?.detail || "Reconcile failed");
                            }
                        }}
                        data-testid="admin-reconcile-books-btn"
                        className="inline-flex items-center gap-2 bg-white border border-black/10 text-[#1C252E] hover:border-[#E53935] rounded-full px-5 py-2 text-sm font-medium transition-all"
                    >
                        <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
                        Reconcile books
                    </button>
                    <button
                        onClick={async () => {
                            try {
                                const { data } = await api.post("/admin/run-recurring-charges");
                                toast.success(`Processed ${data.processed} recurring charge(s)`);
                                reload();
                            } catch (err) {
                                toast.error("Run failed");
                            }
                        }}
                        data-testid="admin-run-recurring-btn"
                        className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-5 py-2 text-sm font-medium transition-all"
                    >
                        <Heart className="w-4 h-4" strokeWidth={1.5} />
                        Run recurring charges
                    </button>
                    <button
                        onClick={() => setManualCheckOpen(true)}
                        data-testid="admin-log-check-btn"
                        className="inline-flex items-center gap-2 bg-white border border-black/10 text-[#1C252E] hover:border-[#E53935] rounded-full px-5 py-2 text-sm font-medium transition-all"
                    >
                        <Mail className="w-4 h-4" strokeWidth={1.5} />
                        Log mailed check
                    </button>
                    <a
                        href={`${API}/donations/export.csv`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="admin-export-donations-btn"
                        className="inline-flex items-center gap-2 bg-[#1C252E] text-white hover:bg-black rounded-full px-5 py-2 text-sm font-medium transition-all"
                    >
                        <Download className="w-4 h-4" strokeWidth={1.5} />
                        Export CSV
                    </a>
                </div>
            </div>
            {/* Mobile (<sm): stacked cards */}
            <div className="sm:hidden space-y-3" data-testid="admin-donations-panel-mobile">
                {items.length === 0 ? (
                    <p className="text-center text-[#4A5568] py-8" data-testid="admin-donations-empty-mobile">No donations yet</p>
                ) : items.map((d) => (
                    <div key={d.id} className="bg-white border border-black/5 rounded-2xl p-4" data-testid={`admin-donation-card-${d.id}`}>
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="min-w-0">
                                <p className="font-medium text-[#1C252E] truncate" data-testid={`admin-donor-name-mobile-${d.id}`}>
                                    {d.donor_name || "—"}
                                    {d.anonymous && (
                                        <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-bold bg-gray-100 text-gray-600 align-middle" title="Hidden from public donor wall & ledger">
                                            anon
                                        </span>
                                    )}
                                </p>
                                <p className="text-xs text-[#4A5568] truncate">{d.donor_email}</p>
                                {(d.source === "paypal" || d.source === "stripe" || d.source === "stripe-subscription") && (
                                    <PaypalVerifiedBadge donation={d} testid={`donation-verified-badge-mobile-${d.id}`} />
                                )}
                                {d.campaign_id && (
                                    <p className={`text-xs mt-0.5 truncate font-medium ${campaignRestricted(d.campaign_id) ? "text-purple-700" : "text-[#4A5568]"}`}>
                                        ◎ {campaignName(d.campaign_id)}{campaignRestricted(d.campaign_id) && " · restricted"}
                                    </p>
                                )}
                                {d.class_tag && (
                                    <p className="text-xs text-[#E53935] mt-0.5 truncate" title={d.class_tag}>
                                        🏆 {d.class_tag}
                                    </p>
                                )}
                            </div>
                            <p className="font-heading text-xl text-[#E53935] font-medium whitespace-nowrap">${d.amount.toFixed(2)}</p>
                        </div>
                        <div className="flex items-center justify-between gap-2 flex-wrap pt-3 border-t border-black/5">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs text-[#4A5568] capitalize">{d.frequency.replace("_", "-")}</span>
                                <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold capitalize ${d.status === "cancelled" ? "bg-gray-100 text-gray-600" : "bg-green-100 text-green-700"}`}>
                                    {d.status || "active"}
                                </span>
                                <span className="text-xs text-[#4A5568]">{new Date(d.created_at).toLocaleDateString()}</span>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                {d.stripe_payment_intent_id && !d.verified && (
                                    <button onClick={() => resyncFromStripe(d)} data-testid={`admin-resync-stripe-mobile-${d.id}`} className="text-xs font-medium text-[#635BFF] hover:underline">Resync</button>
                                )}
                                <button onClick={() => setEditing(d)} data-testid={`admin-edit-donor-mobile-${d.id}`} className="text-xs font-medium text-[#1C252E] hover:underline">Edit</button>
                                <button onClick={() => changeCampaign(d)} data-testid={`admin-change-campaign-mobile-${d.id}`} className="text-xs font-medium text-[#1C252E] hover:underline">Move</button>
                                <button onClick={() => repostJE(d)} data-testid={`admin-repost-je-mobile-${d.id}`} className="text-xs font-medium text-[#1C252E] hover:underline">Re-post</button>
                                {d.frequency === "monthly" && d.status !== "cancelled" && (
                                    <button onClick={() => cancelDonation(d.id)} data-testid={`admin-cancel-donation-mobile-${d.id}`} className="text-xs font-medium text-[#E53935] hover:underline">Cancel</button>
                                )}
                                <button onClick={() => deleteDonation(d)} data-testid={`admin-delete-donation-mobile-${d.id}`} className="text-xs font-medium text-[#E53935] hover:underline">Delete</button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Desktop (≥sm): traditional table */}
            <div className="hidden sm:block bg-white border border-black/5 rounded-2xl overflow-hidden" data-testid="admin-donations-panel">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                            <tr>
                                <th className="text-left px-4 py-3">Donor</th>
                                <th className="text-left px-4 py-3">Campaign</th>
                                <th className="text-left px-4 py-3">Amount</th>
                                <th className="text-left px-4 py-3">Frequency</th>
                                <th className="text-left px-4 py-3">Status</th>
                                <th className="text-left px-4 py-3">Date</th>
                                <th className="text-right px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-black/5">
                            {items.length === 0 && (
                                <tr><td colSpan="7" className="px-4 py-8 text-center text-[#4A5568]" data-testid="admin-donations-empty">No donations yet</td></tr>
                            )}
                            {items.map((d) => (
                                <tr key={d.id} data-testid={`admin-donation-row-${d.id}`}>
                                    <td className="px-4 py-3">
                                        <p className="font-medium text-[#1C252E]" data-testid={`admin-donor-name-${d.id}`}>
                                            {d.donor_name || "—"}
                                            {d.anonymous && (
                                                <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-bold bg-gray-100 text-gray-600 align-middle" title="Hidden from public donor wall & ledger">
                                                    anon
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-xs text-[#4A5568]">{d.donor_email}</p>
                                        {(d.source === "paypal" || d.source === "stripe" || d.source === "stripe-subscription") && (
                                            <PaypalVerifiedBadge donation={d} testid={`donation-verified-badge-${d.id}`} />
                                        )}
                                        {d.class_tag && (
                                            <p className="text-xs text-[#E53935] mt-0.5 truncate" title={d.class_tag}>
                                                🏆 {d.class_tag}
                                            </p>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {d.campaign_id ? (
                                            <span className={`inline-block px-2 py-0.5 rounded-full font-medium ${campaignRestricted(d.campaign_id) ? "bg-purple-50 text-purple-700" : "bg-[#F4F1EA] text-[#1C252E]"}`}>
                                                {campaignName(d.campaign_id)}{campaignRestricted(d.campaign_id) && " ·"}
                                                {campaignRestricted(d.campaign_id) && <span className="ml-1 text-[9px] uppercase tracking-wider font-bold">restricted</span>}
                                            </span>
                                        ) : (
                                            <span className="text-[#4A5568]">General Fund</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 font-heading text-lg font-medium text-[#E53935]">${d.amount.toFixed(2)}</td>
                                    <td className="px-4 py-3 text-[#4A5568] capitalize">{d.frequency.replace("_", "-")}</td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold capitalize ${d.status === "cancelled" ? "bg-gray-100 text-gray-600" : "bg-green-100 text-green-700"}`}>
                                            {d.status || "active"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-[#4A5568]">{new Date(d.created_at).toLocaleDateString()}</td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        {d.stripe_payment_intent_id && !d.verified && (
                                            <button onClick={() => resyncFromStripe(d)} data-testid={`admin-resync-stripe-${d.id}`} className="text-xs font-medium text-[#635BFF] hover:underline ml-2" title="Re-pull from Stripe API">Resync</button>
                                        )}
                                        <button onClick={() => setEditing(d)} data-testid={`admin-edit-donor-${d.id}`} className="text-xs font-medium text-[#1C252E] hover:underline ml-2" title="Edit name / email / anonymity">Edit</button>
                                        <button onClick={() => changeCampaign(d)} data-testid={`admin-change-campaign-${d.id}`} className="text-xs font-medium text-[#1C252E] hover:underline ml-2" title="Move to a different campaign">Move</button>
                                        <button onClick={() => repostJE(d)} data-testid={`admin-repost-je-${d.id}`} className="text-xs font-medium text-[#1C252E] hover:underline ml-2" title="Void & re-post journal entry">Re-post</button>
                                        {d.frequency === "monthly" && d.status !== "cancelled" && (
                                            <button onClick={() => cancelDonation(d.id)} data-testid={`admin-cancel-donation-${d.id}`} className="text-xs font-medium text-[#E53935] hover:underline ml-2">Cancel</button>
                                        )}
                                        <button onClick={() => deleteDonation(d)} data-testid={`admin-delete-donation-${d.id}`} className="text-xs font-medium text-[#E53935] hover:underline ml-2" title="Delete donation & void its journal entry">Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {editing && (
                <EditDonorModal
                    donation={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); reload(); }}
                />
            )}
            {manualCheckOpen && (
                <ManualCheckModal
                    campaigns={campaigns}
                    onClose={() => setManualCheckOpen(false)}
                    onSaved={() => { setManualCheckOpen(false); reload(); }}
                />
            )}
        </div>
    );
};

// Admin modal to record a mailed-in check donation. Creates a donation
// row (payment_method=check, paid) + posts the JE + returns a receipt PDF
// the admin can print and drop in the reply envelope.
const ManualCheckModal = ({ campaigns, onClose, onSaved }) => {
    const [form, setForm] = useState({
        donor_name: "",
        donor_email: "",
        donor_phone: "",
        donor_address: "",
        amount: "",
        check_number: "",
        campaign_id: "",
        notes: "",
        received_date: new Date().toISOString().slice(0, 10),
        send_email_receipt: true,
    });
    const [saving, setSaving] = useState(false);

    const printReceipt = async (donationId) => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API}/admin/donations/${donationId}/receipt.pdf`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) throw new Error("Download failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const w = window.open(url, "_blank");
            if (!w) {
                // Popups blocked — fall back to download.
                const a = document.createElement("a");
                a.href = url;
                a.download = `reddog-receipt-${donationId.slice(0, 8)}.pdf`;
                a.click();
            }
            setTimeout(() => URL.revokeObjectURL(url), 30000);
        } catch {
            toast.error("Could not open receipt PDF");
        }
    };

    const submit = async () => {
        const amt = parseFloat(form.amount);
        if (!form.donor_name.trim()) { toast.error("Donor name required"); return; }
        if (!Number.isFinite(amt) || amt <= 0) { toast.error("Amount must be positive"); return; }
        setSaving(true);
        try {
            const payload = {
                donor_name: form.donor_name.trim(),
                amount: amt,
                donor_email: form.donor_email.trim() || null,
                donor_phone: form.donor_phone.trim(),
                donor_address: form.donor_address.trim(),
                check_number: form.check_number.trim(),
                campaign_id: form.campaign_id || null,
                notes: form.notes.trim(),
                received_date: form.received_date || null,
                send_email_receipt: !!form.send_email_receipt,
            };
            const { data } = await api.post("/admin/donations/manual-check", payload);
            toast.success(`Check logged · $${data.amount.toFixed(2)}`);
            await printReceipt(data.donation_id);
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Log failed");
        } finally {
            setSaving(false);
        }
    };

    const inputCls = "w-full px-3 py-2 border border-black/15 rounded-lg text-sm focus:outline-none focus:border-[#E53935]";

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" data-testid="manual-check-modal">
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-6 border-b border-black/5 flex items-start justify-between">
                    <div>
                        <p className="text-xs uppercase tracking-wider font-bold text-[#E53935]">Physical receipt</p>
                        <h3 className="font-heading text-xl text-[#1C252E] font-medium mt-1">Log a mailed-in check</h3>
                        <p className="text-xs text-[#4A5568] mt-1">Records the deposit, posts the journal entry, and opens a printable receipt PDF you can drop in the reply envelope.</p>
                    </div>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-[#F4F1EA]" data-testid="manual-check-close"><X className="w-5 h-5 text-[#4A5568]" /></button>
                </div>
                <div className="p-6 overflow-y-auto space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">Donor name *</label>
                            <input data-testid="manual-check-donor-name" type="text" value={form.donor_name} onChange={(e) => setForm({ ...form, donor_name: e.target.value })} className={inputCls} placeholder="Jane Smith" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">Amount (USD) *</label>
                            <input data-testid="manual-check-amount" type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls} placeholder="250.00" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">Check # (optional)</label>
                            <input data-testid="manual-check-number" type="text" value={form.check_number} onChange={(e) => setForm({ ...form, check_number: e.target.value })} className={inputCls} placeholder="4521" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">Received date</label>
                            <input data-testid="manual-check-date" type="date" value={form.received_date} onChange={(e) => setForm({ ...form, received_date: e.target.value })} className={inputCls} />
                        </div>
                        <div>
                            <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">Donor email (optional)</label>
                            <input data-testid="manual-check-email" type="email" value={form.donor_email} onChange={(e) => setForm({ ...form, donor_email: e.target.value })} className={inputCls} placeholder="jane@example.com" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">Donor phone</label>
                            <input data-testid="manual-check-phone" type="text" value={form.donor_phone} onChange={(e) => setForm({ ...form, donor_phone: e.target.value })} className={inputCls} placeholder="(406) 555-1234" />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">Mailing address (for the reply envelope)</label>
                            <input data-testid="manual-check-address" type="text" value={form.donor_address} onChange={(e) => setForm({ ...form, donor_address: e.target.value })} className={inputCls} placeholder="123 Main St, Frenchtown MT 59834" />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">Campaign (optional)</label>
                            <select data-testid="manual-check-campaign" value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })} className={inputCls}>
                                <option value="">General Fund (unrestricted)</option>
                                {campaigns.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}{c.restricted_fund ? " · restricted" : ""}</option>
                                ))}
                            </select>
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">Notes (internal)</label>
                            <textarea data-testid="manual-check-notes" rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} placeholder="Received in the mail Feb 15, deposited Feb 17" />
                        </div>
                        <div className="sm:col-span-2 flex items-center gap-2 pt-1">
                            <input id="manual-check-send-email" type="checkbox" checked={form.send_email_receipt} onChange={(e) => setForm({ ...form, send_email_receipt: e.target.checked })} data-testid="manual-check-send-email" className="w-4 h-4" />
                            <label htmlFor="manual-check-send-email" className="text-xs text-[#4A5568]">Also email the donor a digital copy (if email provided)</label>
                        </div>
                    </div>
                </div>
                <div className="p-4 border-t border-black/5 flex justify-end gap-2 bg-[#FDFBF7]">
                    <button onClick={onClose} className="px-5 py-2 rounded-full border border-black/15 text-sm hover:bg-[#F4F1EA]" data-testid="manual-check-cancel">Cancel</button>
                    <button onClick={submit} disabled={saving} data-testid="manual-check-submit" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#E53935] text-white text-sm font-medium disabled:opacity-40">
                        {saving ? <RotateCw className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                        Log & print receipt
                    </button>
                </div>
            </div>
        </div>
    );
};

// Admin-only modal for correcting donor name / email / anonymous flag on a
// single donation row. Backed by PUT /api/admin/donations/{id}/donor.
const EditDonorModal = ({ donation, onClose, onSaved }) => {
    const [name, setName] = useState(donation.donor_name || "");
    const [email, setEmail] = useState(donation.donor_email || "");
    const [anon, setAnon] = useState(!!donation.anonymous);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/admin/donations/${donation.id}/donor`, {
                donor_name: name.trim(),
                donor_email: email.trim().toLowerCase(),
                anonymous: anon,
            });
            toast.success("Donor info updated");
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="edit-donor-modal"
            className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3 mb-5">
                    <div>
                        <h3 className="font-heading text-2xl text-[#1C252E]">Edit donor info</h3>
                        <p className="text-xs text-[#4A5568] mt-1">
                            ${donation.amount.toFixed(2)} · {donation.frequency.replace("_", "-")} · {new Date(donation.created_at).toLocaleDateString()}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-black/5" aria-label="Close" data-testid="edit-donor-close">
                        <X className="w-5 h-5 text-[#4A5568]" />
                    </button>
                </div>

                <div className="space-y-4 mb-6">
                    <div>
                        <label htmlFor="edit-donor-name" className="block text-[11px] uppercase tracking-wider font-bold text-[#4A5568] mb-1.5">Donor name</label>
                        <input
                            id="edit-donor-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            data-testid="edit-donor-name-input"
                            className="w-full rounded-xl border border-black/10 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                        />
                    </div>
                    <div>
                        <label htmlFor="edit-donor-email" className="block text-[11px] uppercase tracking-wider font-bold text-[#4A5568] mb-1.5">Donor email</label>
                        <input
                            id="edit-donor-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="edit-donor-email-input"
                            className="w-full rounded-xl border border-black/10 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                        />
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border border-black/5 hover:bg-[#FAF6EE]">
                        <input
                            type="checkbox"
                            checked={anon}
                            onChange={(e) => setAnon(e.target.checked)}
                            data-testid="edit-donor-anonymous-toggle"
                            className="w-4 h-4 accent-[#E53935]"
                        />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#1C252E]">Show as Anonymous publicly</p>
                            <p className="text-xs text-[#4A5568]">Hides this donor on the public donor wall, ledger, and tribute feed. Real name + email are still visible here in admin.</p>
                        </div>
                    </label>
                </div>

                <div className="flex gap-3">
                    <button onClick={onClose} data-testid="edit-donor-cancel-btn" className="flex-1 px-6 py-2.5 rounded-full border border-black/10 text-[#1C252E] hover:border-black/30 transition-colors text-sm font-medium">Cancel</button>
                    <button onClick={save} disabled={saving} data-testid="edit-donor-save-btn" className="flex-1 px-6 py-2.5 rounded-full bg-[#E53935] text-white hover:bg-[#D32F2F] disabled:opacity-60 transition-colors text-sm font-medium">
                        {saving ? "Saving…" : "Save changes"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Tiny labeled-field wrapper for the new fundraiser modal — matches the
// pattern used by the contractor / spend-request / credentials modals.
const CmFld = ({ label, hint, children }) => (
    <div>
        <label className="block text-[11px] uppercase tracking-wider font-bold text-[#4A5568] mb-1.5">{label}</label>
        {children}
        {hint && <p className="text-[11px] text-[#4A5568] mt-1 leading-snug">{hint}</p>}
    </div>
);


const CampaignsPanel = () => {
    const [items, setItems] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);   // null = creating; string = editing this campaign
    const [form, setForm] = useState({ name: "", slug: "", description: "", goal_amount: 1000, ends_at: "", image_url: "", coupon_code: "" });
    const [progress, setProgress] = useState({});
    const [showPicker, setShowPicker] = useState(false);
    const [updatesFor, setUpdatesFor] = useState(null); // campaign object when Updates modal open

    const load = async () => {
        try {
            const { data } = await api.get("/campaigns");
            setItems(data);
            // For each campaign, also fetch public progress
            const map = {};
            await Promise.all(
                data.map(async (c) => {
                    try {
                        const { data: p } = await api.get(`/campaigns/${c.id}/public`);
                        map[c.id] = p;
                    } catch {}
                })
            );
            setProgress(map);
        } catch {}
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditingId(null);
        setForm({ name: "", slug: "", description: "", goal_amount: 1000, ends_at: "", image_url: "", coupon_code: "" });
        setShowForm(true);
    };

    const openEdit = (c) => {
        setEditingId(c.id);
        setForm({
            name: c.name || "",
            slug: c.slug || "",
            description: c.description || "",
            goal_amount: c.goal_amount ?? 0,
            // Convert ISO → datetime-local format (YYYY-MM-DDTHH:mm)
            ends_at: c.ends_at ? new Date(c.ends_at).toISOString().slice(0, 16) : "",
            image_url: c.image_url || "",
            coupon_code: c.coupon_code || "",
        });
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditingId(null);
    };

    const save = async (e) => {
        e.preventDefault();
        const body = {
            name: form.name,
            slug: form.slug.trim().toLowerCase().replace(/\s+/g, "-") || form.name.trim().toLowerCase().replace(/\s+/g, "-"),
            description: form.description,
            goal_amount: parseFloat(form.goal_amount),
            ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
            image_url: form.image_url,
            coupon_code: form.coupon_code || null,
            active: true,
            featured: true,
        };
        try {
            if (editingId) {
                // PATCH — slug is intentionally NOT in the allowed-edit set on the backend
                // (changing it breaks bookmarks / QR posters). Send everything else.
                const { slug, ...editable } = body;  // eslint-disable-line no-unused-vars
                await api.patch(`/campaigns/${editingId}`, editable);
                toast.success("Fundraiser updated");
            } else {
                await api.post("/campaigns", body);
                toast.success("Fundraiser created");
            }
            closeForm();
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || (editingId ? "Failed to update" : "Failed to create"));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this campaign?")) return;
        await api.delete(`/campaigns/${id}`);
        toast.success("Deleted");
        load();
    };

    return (
        <div data-testid="admin-campaigns-panel">
            <div className="flex justify-between items-center mb-6">
                <p className="text-sm text-[#4A5568]">{items.length} campaign(s)</p>
                <div className="flex items-center gap-2">
                    {items.length > 0 && (
                        <button
                            type="button"
                            onClick={async () => {
                                try {
                                    const token = localStorage.getItem("token");
                                    const r = await fetch(`${API}/admin/campaigns/qr-sheet.pdf`, {
                                        headers: { Authorization: `Bearer ${token}` },
                                    });
                                    if (!r.ok) throw new Error(await r.text());
                                    const blob = await r.blob();
                                    window.open(URL.createObjectURL(blob), "_blank");
                                    toast.success("Campaign QR sheet ready");
                                } catch (e) {
                                    toast.error(`QR sheet failed: ${e.message || e}`);
                                }
                            }}
                            data-testid="admin-campaigns-qr-sheet"
                            className="inline-flex items-center gap-2 bg-white text-[#1C252E] border border-black/15 hover:bg-[#F4F1EA] rounded-full px-4 py-2 text-xs font-medium transition-all"
                            title="Print a laminate-ready sheet with every active campaign's QR code"
                        >
                            <QrCode className="w-4 h-4" strokeWidth={1.5} />
                            Print QR sheet
                        </button>
                    )}
                    <button
                        onClick={openCreate}
                        data-testid="admin-add-campaign-btn"
                        className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-6 py-2.5 text-sm font-medium transition-all"
                    >
                        <Plus className="w-4 h-4" strokeWidth={2} />
                        New campaign
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {items.length === 0 && (
                    <div className="lg:col-span-2 bg-white border border-black/5 rounded-2xl p-10 text-center text-[#4A5568]" data-testid="admin-campaigns-empty">
                        No campaigns yet — create one to power your next fundraiser
                    </div>
                )}
                {items.map((c) => {
                    const p = progress[c.id] || { raised: 0, percent: 0, donors: 0, days_left: null };
                    return (
                        <div key={c.id} data-testid={`admin-campaign-row-${c.id}`} className="bg-white border border-black/5 rounded-2xl p-6">
                            <div className="flex items-start justify-between gap-3 mb-2">
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-heading text-xl text-[#1C252E] font-medium truncate">{c.name}</h3>
                                    <p className="text-xs text-[#4A5568] font-mono">/{c.slug} {c.coupon_code && <span className="ml-1 text-[#E53935]">· {c.coupon_code}</span>}</p>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => setUpdatesFor(c)} data-testid={`admin-campaign-updates-${c.id}`} aria-label={`Post an update for ${c.name}`} title="Post an update" className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#1C252E]">
                                        <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                    <button onClick={() => openEdit(c)} data-testid={`admin-campaign-edit-${c.id}`} aria-label={`Edit campaign ${c.name}`} title="Edit fundraiser" className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#1C252E]">
                                        <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                    <a
                                        href={`${API}/campaigns/${c.id}/qr.png?size=512`}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`admin-campaign-qr-${c.id}`}
                                        title="Download raw QR code PNG (for flyers)"
                                        aria-label={`Download QR code for ${c.name}`}
                                        className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#1C252E]"
                                    >
                                        <QrCode className="w-4 h-4" strokeWidth={1.5} />
                                    </a>
                                    <a
                                        href={`${API}/campaigns/${c.slug}/poster.pdf`}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`admin-campaign-poster-${c.id}`}
                                        title="Download QR poster (PDF)"
                                        aria-label={`Download QR poster for ${c.name}`}
                                        className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#1C252E]"
                                    >
                                        <Download className="w-4 h-4" strokeWidth={1.5} />
                                    </a>
                                    <button onClick={() => remove(c.id)} data-testid={`admin-campaign-delete-${c.id}`} aria-label={`Delete campaign ${c.name}`} className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#E53935]">
                                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                </div>
                            </div>
                            <div className="mt-4">
                                <div className="flex items-baseline justify-between mb-2">
                                    <p className="text-sm text-[#1C252E]">
                                        <span className="font-bold">${Math.round(p.raised).toLocaleString()}</span>
                                        <span className="text-[#4A5568]"> / ${Math.round(c.goal_amount).toLocaleString()}</span>
                                    </p>
                                    <p className="text-sm font-bold text-[#E53935]">{Math.round(p.percent)}%</p>
                                </div>
                                <div className="h-2 w-full bg-[#F4F1EA] rounded-full overflow-hidden">
                                    <div className="h-full bg-[#E53935] rounded-full" style={{ width: `${Math.min(p.percent, 100)}%` }} />
                                </div>
                                <p className="text-xs text-[#4A5568] mt-2">
                                    {p.donors} donor(s){p.days_left !== null && ` · ${p.days_left} days left`}
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>

            {showForm && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeForm} data-testid="admin-campaign-modal">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        {/* Sticky header */}
                        <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-black/5 flex-shrink-0 flex items-start justify-between gap-4">
                            <div>
                                <h3 className="font-heading text-xl text-[#1C252E] font-medium">{editingId ? "Edit fundraiser" : "New fundraiser"}</h3>
                                <p className="text-xs text-[#4A5568] mt-1">
                                    {editingId
                                        ? "Update the donor-facing copy, goal, or image. URL slug stays fixed so existing QR posters keep working."
                                        : "Create a fundraiser donors can give toward · gets a QR poster + share page on /fundraise."}
                                </p>
                            </div>
                            <button onClick={closeForm} className="p-2"><X className="w-4 h-4" /></button>
                        </div>

                        {/* Scrollable body */}
                        <form id="campaign-form" onSubmit={save} className="px-6 sm:px-8 py-5 overflow-y-auto flex-1 space-y-6">
                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">Basics</h4>
                                <CmFld label="Fundraiser name *" hint="Donor-facing title shown on /fundraise.">
                                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Spring Field Trip Fund" data-testid="campaign-form-name" className="w-full rounded-xl border border-black/10 px-4 py-2.5" />
                                </CmFld>
                                <CmFld label="URL slug" hint={editingId ? "Locked after creation — changing it would break existing QR posters and bookmarks." : "Lowercase, hyphens — auto-generated from the name if blank."}>
                                    <input
                                        value={form.slug}
                                        onChange={(e) => setForm({ ...form, slug: e.target.value })}
                                        disabled={!!editingId}
                                        placeholder="spring-field-trip"
                                        data-testid="campaign-form-slug"
                                        className={`w-full rounded-xl border border-black/10 px-4 py-2.5 font-mono text-sm ${editingId ? "bg-[#F4F1EA] text-[#4A5568] cursor-not-allowed" : ""}`}
                                    />
                                </CmFld>
                                <CmFld label="Description" hint="2–3 sentences telling donors what this fundraiser is for.">
                                    <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="campaign-form-description" className="w-full rounded-xl border border-black/10 px-4 py-2.5 resize-none" />
                                </CmFld>
                            </section>

                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">Goal &amp; timing</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <CmFld label="Goal amount" hint="Leave at $0 for a flexible fund (no thermometer).">
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]">$</span>
                                            <input required type="number" min="0" step="1" value={form.goal_amount} onChange={(e) => setForm({ ...form, goal_amount: e.target.value })} data-testid="campaign-form-goal" className="w-full rounded-xl border border-black/10 pl-7 pr-3 py-2.5 font-mono" />
                                        </div>
                                    </CmFld>
                                    <CmFld label="Ends on" hint="Optional. Drives the 'days left' counter.">
                                        <input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="campaign-form-ends" className="w-full rounded-xl border border-black/10 px-3 py-2.5 text-sm" />
                                    </CmFld>
                                </div>
                            </section>

                            <section className="space-y-3">
                                <h4 className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#E53935]">Imagery &amp; extras</h4>
                                <CmFld label="Cover image" hint="Used on /fundraise card, share page, and QR poster.">
                                    <div className="flex gap-2">
                                        <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://… or pick from library" data-testid="campaign-form-image" className="flex-1 rounded-xl border border-black/10 px-4 py-2.5" />
                                        <button type="button" onClick={() => setShowPicker(true)} data-testid="campaign-form-pick-library" className="px-4 py-2.5 rounded-xl border border-black/10 hover:bg-[#F4F1EA] text-sm font-medium whitespace-nowrap">
                                            Pick from library
                                        </button>
                                    </div>
                                </CmFld>
                                <CmFld label="Linked coupon code" hint="Optional. Auto-applies on the shop when checking out tied to this campaign.">
                                    <input value={form.coupon_code} onChange={(e) => setForm({ ...form, coupon_code: e.target.value.toUpperCase() })} placeholder="e.g. PTA10" data-testid="campaign-form-coupon" className="w-full rounded-xl border border-black/10 px-4 py-2.5 uppercase font-mono" />
                                </CmFld>
                            </section>
                        </form>

                        {/* Sticky footer */}
                        <div className="px-6 sm:px-8 py-4 border-t border-black/5 flex items-center justify-end gap-3 flex-shrink-0 bg-[#FDFBF7] rounded-b-3xl">
                            <button onClick={closeForm} className="text-sm font-medium text-[#4A5568] hover:text-[#1C252E] px-4 py-2">Cancel</button>
                            <button form="campaign-form" type="submit" data-testid="campaign-form-submit" className="bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-6 py-2.5 text-sm font-medium">
                                {editingId ? "Save changes" : "Create fundraiser"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <ImagePicker
                open={showPicker}
                onClose={() => setShowPicker(false)}
                onPick={(img) => setForm((prev) => ({ ...prev, image_url: img.url }))}
                category="campaigns"
            />

            {updatesFor && (
                <CampaignUpdatesModal
                    campaign={updatesFor}
                    onClose={() => setUpdatesFor(null)}
                />
            )}
        </div>
    );
};

const CampaignUpdatesModal = ({ campaign, onClose }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ title: "", body: "", image_url: "" });
    const [posting, setPosting] = useState(false);

    const load = () => {
        setLoading(true);
        api.get(`/campaigns/${campaign.id}/updates`)
            .then((r) => setItems(r.data || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [campaign.id]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.body.trim()) {
            toast.error("Title and body are required");
            return;
        }
        setPosting(true);
        try {
            await api.post(`/campaigns/${campaign.id}/updates`, {
                title: form.title.trim(),
                body: form.body.trim(),
                image_url: form.image_url.trim() || null,
            });
            toast.success("Update posted");
            setForm({ title: "", body: "", image_url: "" });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to post update");
        } finally {
            setPosting(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this update?")) return;
        try {
            await api.delete(`/campaigns/${campaign.id}/updates/${id}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete");
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-campaign-updates-modal">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-black/5 flex-shrink-0 flex items-start justify-between gap-4">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[#E53935] font-bold">Campaign updates</p>
                        <h3 className="font-heading text-xl text-[#1C252E] font-medium mt-1">{campaign.name}</h3>
                        <p className="text-xs text-[#4A5568] mt-1">Posts show up in the public timeline on <span className="font-mono">/c/{campaign.slug}</span></p>
                    </div>
                    <button onClick={onClose} data-testid="admin-campaign-updates-close" aria-label="Close" className="p-2 rounded-full hover:bg-[#F4F1EA]">
                        <X className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 sm:px-8 py-6 overflow-y-auto space-y-6">
                    {/* Composer */}
                    <form onSubmit={submit} className="space-y-3" data-testid="admin-campaign-updates-form">
                        <input
                            type="text"
                            required
                            maxLength={200}
                            placeholder="Title (e.g. Halfway to goal!)"
                            value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            data-testid="admin-campaign-update-title"
                            className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                        />
                        <textarea
                            required
                            rows={4}
                            maxLength={4000}
                            placeholder="What happened this week? Share progress, thank donors, post photos of the work."
                            value={form.body}
                            onChange={(e) => setForm({ ...form, body: e.target.value })}
                            data-testid="admin-campaign-update-body"
                            className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 resize-none"
                        />
                        <input
                            type="url"
                            placeholder="Optional image URL (paste an already-uploaded image URL)"
                            value={form.image_url}
                            onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                            data-testid="admin-campaign-update-image"
                            className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]/40"
                        />
                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={posting}
                                data-testid="admin-campaign-update-post"
                                className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] disabled:opacity-60 text-white rounded-full px-6 py-2.5 text-sm font-medium transition-all"
                            >
                                <Send className="w-4 h-4" strokeWidth={2} />
                                {posting ? "Posting…" : "Post update"}
                            </button>
                        </div>
                    </form>

                    {/* Timeline */}
                    <div className="pt-4 border-t border-black/5">
                        <p className="text-xs uppercase tracking-wider text-[#4A5568] font-bold mb-3">Timeline ({items.length})</p>
                        {loading ? (
                            <p className="text-sm text-[#4A5568]" data-testid="admin-campaign-updates-loading">Loading…</p>
                        ) : items.length === 0 ? (
                            <p className="text-sm text-[#4A5568]" data-testid="admin-campaign-updates-empty">No updates posted yet.</p>
                        ) : (
                            <ul className="space-y-3" data-testid="admin-campaign-updates-list">
                                {items.map((u) => (
                                    <li key={u.id} className="bg-[#F4F1EA] rounded-xl p-4" data-testid={`admin-campaign-update-row-${u.id}`}>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <p className="font-medium text-[#1C252E]">{u.title}</p>
                                                <p className="text-xs text-[#4A5568] mt-0.5">
                                                    {new Date(u.created_at).toLocaleString()} · {u.author_name || "Foundation"}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => remove(u.id)}
                                                data-testid={`admin-campaign-update-delete-${u.id}`}
                                                aria-label="Delete update"
                                                className="p-1.5 rounded-full hover:bg-white/60 text-[#E53935] flex-shrink-0"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                            </button>
                                        </div>
                                        <p className="text-sm text-[#1C252E] mt-2 whitespace-pre-wrap">{u.body}</p>
                                        {u.image_url && (
                                            <img src={u.image_url} alt="" className="mt-3 rounded-lg max-h-40 object-cover" />
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const CouponsPanel = () => {
    const [items, setItems] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ code: "", discount_type: "percent", discount_value: 10, min_subtotal: 0, max_uses: "", campaign: "", start_date: "", expires_at: "" });

    const load = () => api.get("/coupons").then((r) => setItems(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        const body = {
            code: form.code.toUpperCase().trim(),
            discount_type: form.discount_type,
            discount_value: parseFloat(form.discount_value),
            min_subtotal: parseFloat(form.min_subtotal) || 0,
            max_uses: form.max_uses ? parseInt(form.max_uses) : null,
            campaign: form.campaign,
            start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
            expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
            active: true,
        };
        try {
            await api.post("/coupons", body);
            toast.success(`Coupon ${body.code} created`);
            setShowForm(false);
            setForm({ code: "", discount_type: "percent", discount_value: 10, min_subtotal: 0, max_uses: "", campaign: "", start_date: "", expires_at: "" });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to create coupon");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this coupon?")) return;
        await api.delete(`/coupons/${id}`);
        toast.success("Deleted");
        load();
    };

    return (
        <div data-testid="admin-coupons-panel">
            <div className="flex justify-between items-center mb-6">
                <p className="text-sm text-[#4A5568]">{items.length} coupon code(s)</p>
                <button
                    onClick={() => setShowForm(true)}
                    data-testid="admin-add-coupon-btn"
                    className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-6 py-2.5 text-sm font-medium transition-all"
                >
                    <Plus className="w-4 h-4" strokeWidth={2} />
                    New campaign code
                </button>
            </div>

            <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                            <tr>
                                <th className="text-left px-4 py-3">Code</th>
                                <th className="text-left px-4 py-3">Discount</th>
                                <th className="text-left px-4 py-3">Min</th>
                                <th className="text-left px-4 py-3">Used / Max</th>
                                <th className="text-left px-4 py-3">Campaign</th>
                                <th className="text-right px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-black/5">
                            {items.length === 0 && (
                                <tr><td colSpan="6" className="px-4 py-8 text-center text-[#4A5568]" data-testid="admin-coupons-empty">No coupons yet — create one to power your next fundraiser</td></tr>
                            )}
                            {items.map((c) => (
                                <tr key={c.id} data-testid={`admin-coupon-row-${c.id}`}>
                                    <td className="px-4 py-3 font-mono font-bold text-[#1C252E]">{c.code}</td>
                                    <td className="px-4 py-3 text-[#1C252E]">
                                        {c.discount_type === "percent" ? `${c.discount_value}%` : `$${c.discount_value.toFixed(2)}`}
                                    </td>
                                    <td className="px-4 py-3 text-[#4A5568]">${c.min_subtotal.toFixed(2)}</td>
                                    <td className="px-4 py-3 text-[#4A5568]">{c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                                    <td className="px-4 py-3 text-[#4A5568]">{c.campaign || "—"}</td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => remove(c.id)} data-testid={`admin-coupon-delete-${c.id}`} className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#E53935]">
                                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {showForm && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)} data-testid="admin-coupon-modal">
                    <div className="bg-white rounded-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="font-heading text-2xl text-[#1C252E] font-medium">New coupon</h3>
                            <button onClick={() => setShowForm(false)} className="p-2"><X className="w-4 h-4" /></button>
                        </div>
                        <form onSubmit={save} className="space-y-4">
                            <input required placeholder="Code (e.g. SPRING20)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="coupon-form-code" className="w-full rounded-xl border border-black/10 px-4 py-3 uppercase font-mono" />
                            <div className="grid grid-cols-2 gap-3">
                                <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} data-testid="coupon-form-type" className="rounded-xl border border-black/10 px-4 py-3 bg-white">
                                    <option value="percent">Percent off</option>
                                    <option value="fixed">Fixed $ off</option>
                                </select>
                                <input required type="number" min="0" step="0.01" placeholder={form.discount_type === "percent" ? "%" : "$"} value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} data-testid="coupon-form-value" className="rounded-xl border border-black/10 px-4 py-3" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <input type="number" min="0" step="0.01" placeholder="Min subtotal $" value={form.min_subtotal} onChange={(e) => setForm({ ...form, min_subtotal: e.target.value })} data-testid="coupon-form-min" className="rounded-xl border border-black/10 px-4 py-3" />
                                <input type="number" min="1" placeholder="Max uses (blank=∞)" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="coupon-form-maxuses" className="rounded-xl border border-black/10 px-4 py-3" />
                            </div>
                            <input placeholder="Campaign name (e.g. Spring Drive)" value={form.campaign} onChange={(e) => setForm({ ...form, campaign: e.target.value })} data-testid="coupon-form-campaign" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs uppercase tracking-[0.12em] font-bold text-[#4A5568] block mb-1">Activates on</label>
                                    <input type="datetime-local" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="coupon-form-start-date" className="w-full rounded-xl border border-black/10 px-3 py-2.5 text-sm" />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-[0.12em] font-bold text-[#4A5568] block mb-1">Expires</label>
                                    <input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="coupon-form-expires-at" className="w-full rounded-xl border border-black/10 px-3 py-2.5 text-sm" />
                                </div>
                            </div>
                            <button type="submit" data-testid="coupon-form-submit" className="w-full bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full py-3 font-medium">
                                Create coupon
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

const EventsPanel = () => {
    const [items, setItems] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ title: "", description: "", starts_at: "", location: "", image_url: "", rsvp_link: "" });

    const load = () => api.get("/events").then((r) => setItems(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            await api.post("/events", {
                ...form,
                starts_at: new Date(form.starts_at).toISOString(),
            });
            toast.success("Event created");
            setShowForm(false);
            setForm({ title: "", description: "", starts_at: "", location: "", image_url: "", rsvp_link: "" });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to create event");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this event?")) return;
        await api.delete(`/events/${id}`);
        toast.success("Deleted");
        load();
    };

    return (
        <div data-testid="admin-events-panel">
            <div className="flex justify-between items-center mb-6">
                <p className="text-sm text-[#4A5568]">{items.length} event(s)</p>
                <button
                    onClick={() => setShowForm(true)}
                    data-testid="admin-add-event-btn"
                    className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-6 py-2.5 text-sm font-medium transition-all"
                >
                    <Plus className="w-4 h-4" strokeWidth={2} />
                    New event
                </button>
            </div>
            <div className="space-y-3">
                {items.length === 0 && <p className="text-center text-[#4A5568] py-8" data-testid="admin-events-empty">No events scheduled yet.</p>}
                {items.map((e) => (
                    <div key={e.id} className="bg-white border border-black/5 rounded-2xl p-5 flex justify-between gap-4" data-testid={`admin-event-row-${e.id}`}>
                        <div className="flex-1 min-w-0">
                            <p className="font-heading text-lg text-[#1C252E] font-medium">{e.title}</p>
                            <p className="text-xs text-[#4A5568] mt-0.5">
                                {new Date(e.starts_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                                {e.location && ` · ${e.location}`}
                            </p>
                            {e.description && <p className="text-sm text-[#4A5568] mt-2 line-clamp-2">{e.description}</p>}
                        </div>
                        <div className="flex items-start gap-2 flex-shrink-0">
                            <Link
                                to={`/admin/events/${e.id}/checkin`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`admin-event-checkin-${e.id}`}
                                className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1C252E] hover:text-[#E53935] border border-black/10 rounded-full px-3 py-1.5 transition-colors whitespace-nowrap"
                                title="Open mobile check-in"
                            >
                                Check-in
                            </Link>
                            <button onClick={() => remove(e.id)} data-testid={`admin-event-delete-${e.id}`} className="p-2 text-[#E53935] hover:bg-[#F4F1EA] rounded-full">
                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {showForm && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)} data-testid="admin-event-modal">
                    <div className="bg-white rounded-2xl p-8 max-w-md w-full" onClick={(evt) => evt.stopPropagation()}>
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="font-heading text-2xl text-[#1C252E] font-medium">New event</h3>
                            <button onClick={() => setShowForm(false)} className="p-2"><X className="w-4 h-4" /></button>
                        </div>
                        <form onSubmit={save} className="space-y-4">
                            <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-form-title" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <textarea placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="event-form-description" className="w-full rounded-xl border border-black/10 px-4 py-3 resize-none" />
                            <input required type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="event-form-starts" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="event-form-location" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <input placeholder="RSVP link (optional)" value={form.rsvp_link} onChange={(e) => setForm({ ...form, rsvp_link: e.target.value })} data-testid="event-form-rsvp" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <button type="submit" data-testid="event-form-submit" className="w-full bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full py-3 font-medium">Create event</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

const VolunteersPanel = () => {
    const [items, setItems] = useState([]);
    const [issuing, setIssuing] = useState(null); // volunteer id being issued
    useEffect(() => { api.get("/volunteers").then((r) => setItems(r.data)).catch(() => {}); }, []);

    const issueLink = async (v, notify) => {
        setIssuing(v.id);
        try {
            const r = await api.post(`/admin/volunteers/${v.id}/issue-portal-link`, {
                ttl_days: 14,
                notify_by_email: notify,
            });
            if (notify) {
                toast.success(`Dashboard link emailed to ${v.email}`);
            } else {
                try {
                    await navigator.clipboard.writeText(r.data.url);
                    toast.success("Dashboard link copied to clipboard");
                } catch {
                    toast.success("Dashboard link ready — check response");
                }
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't issue link");
        } finally {
            setIssuing(null);
        }
    };

    return (
        <div data-testid="admin-volunteers-panel">
            <p className="text-sm text-[#4A5568] mb-6">{items.length} volunteer signup(s)</p>
            <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                            <tr>
                                <th className="text-left px-4 py-3">Name</th>
                                <th className="text-left px-4 py-3">Contact</th>
                                <th className="text-left px-4 py-3">Interest</th>
                                <th className="text-left px-4 py-3">Date</th>
                                <th className="text-left px-4 py-3">Dashboard</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-black/5">
                            {items.length === 0 && (
                                <tr><td colSpan="5" className="px-4 py-8 text-center text-[#4A5568]" data-testid="admin-volunteers-empty">No volunteer signups yet</td></tr>
                            )}
                            {items.map((v) => (
                                <tr key={v.id} data-testid={`admin-volunteer-row-${v.id}`}>
                                    <td className="px-4 py-3 font-medium text-[#1C252E]">{v.name}</td>
                                    <td className="px-4 py-3 text-[#4A5568]">
                                        <p>{v.email}</p>
                                        {v.phone && <p className="text-xs">{v.phone}</p>}
                                    </td>
                                    <td className="px-4 py-3 text-[#4A5568]">
                                        <p>{v.role || "—"}</p>
                                        {v.message && <p className="text-xs truncate max-w-[280px]">{v.message}</p>}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-[#4A5568]">{new Date(v.created_at).toLocaleDateString()}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex gap-1.5">
                                            <button
                                                onClick={() => issueLink(v, true)}
                                                disabled={issuing === v.id}
                                                data-testid={`admin-volunteer-email-link-${v.id}`}
                                                className="text-[11px] bg-[#1C252E] hover:bg-black text-white rounded-full px-3 py-1 disabled:opacity-60"
                                            >
                                                Email link
                                            </button>
                                            <button
                                                onClick={() => issueLink(v, false)}
                                                disabled={issuing === v.id}
                                                data-testid={`admin-volunteer-copy-link-${v.id}`}
                                                className="text-[11px] bg-white border border-black/15 hover:border-[#1C252E] text-[#1C252E] rounded-full px-3 py-1 disabled:opacity-60"
                                            >
                                                Copy
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const NewsletterPanel = () => {
    const [items, setItems] = useState([]);
    useEffect(() => { api.get("/newsletter").then((r) => setItems(r.data)).catch(() => {}); }, []);
    return (
        <div data-testid="admin-newsletter-panel">
            <p className="text-sm text-[#4A5568] mb-6">{items.length} subscriber(s)</p>
            <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F4F1EA] text-xs uppercase tracking-wider text-[#4A5568]">
                            <tr>
                                <th className="text-left px-4 py-3">Email</th>
                                <th className="text-left px-4 py-3">Source</th>
                                <th className="text-left px-4 py-3">Subscribed</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-black/5">
                            {items.length === 0 && (
                                <tr><td colSpan="3" className="px-4 py-8 text-center text-[#4A5568]" data-testid="admin-newsletter-empty">No subscribers yet</td></tr>
                            )}
                            {items.map((s) => (
                                <tr key={s.id} data-testid={`admin-newsletter-row-${s.id}`}>
                                    <td className="px-4 py-3 font-medium text-[#1C252E]">{s.email}</td>
                                    <td className="px-4 py-3 text-[#4A5568]">{s.source}</td>
                                    <td className="px-4 py-3 text-xs text-[#4A5568]">{new Date(s.created_at).toLocaleDateString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const MatchesPanel = () => {
    const [items, setItems] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ name: "", sponsor: "", multiplier: 2, max_match_amount: 1000, starts_at: "", ends_at: "" });

    const load = () => api.get("/matches").then((r) => setItems(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            await api.post("/matches", {
                name: form.name,
                sponsor: form.sponsor,
                multiplier: parseFloat(form.multiplier),
                max_match_amount: parseFloat(form.max_match_amount),
                starts_at: new Date(form.starts_at).toISOString(),
                ends_at: new Date(form.ends_at).toISOString(),
                active: true,
            });
            toast.success("Match window created");
            setShowForm(false);
            setForm({ name: "", sponsor: "", multiplier: 2, max_match_amount: 1000, starts_at: "", ends_at: "" });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Create failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this match?")) return;
        await api.delete(`/matches/${id}`);
        toast.success("Deleted");
        load();
    };

    const now = new Date();

    return (
        <div data-testid="admin-matches-panel">
            <div className="flex justify-between items-center mb-6">
                <p className="text-sm text-[#4A5568]">{items.length} match window(s)</p>
                <button
                    onClick={() => setShowForm(true)}
                    data-testid="admin-add-match-btn"
                    className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-6 py-2.5 text-sm font-medium transition-all"
                >
                    <Plus className="w-4 h-4" strokeWidth={2} />
                    New match window
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {items.length === 0 && (
                    <div className="lg:col-span-2 bg-white border border-black/5 rounded-2xl p-10 text-center text-[#4A5568]" data-testid="admin-matches-empty">
                        No match windows yet — partner with a local business and double the impact
                    </div>
                )}
                {items.map((m) => {
                    const start = new Date(m.starts_at);
                    const end = new Date(m.ends_at);
                    const isActive = start <= now && now <= end && m.matched_so_far < m.max_match_amount;
                    const poolPct = Math.min((m.matched_so_far / m.max_match_amount) * 100, 100);
                    return (
                        <div key={m.id} className="bg-white border border-black/5 rounded-2xl p-6" data-testid={`admin-match-row-${m.id}`}>
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs uppercase tracking-wider text-[#E53935] font-bold mb-1">{m.multiplier}x match</p>
                                    <h3 className="font-heading text-lg text-[#1C252E] font-medium truncate">{m.name}</h3>
                                    <p className="text-xs text-[#4A5568]">by {m.sponsor}</p>
                                </div>
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                                    {isActive ? "Live" : (start > now ? "Scheduled" : "Ended")}
                                </span>
                                <button onClick={() => remove(m.id)} data-testid={`admin-match-delete-${m.id}`} className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#E53935]">
                                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                            </div>
                            <div className="text-xs text-[#4A5568] mb-3">
                                {start.toLocaleDateString()} → {end.toLocaleDateString()}
                            </div>
                            <div className="flex items-baseline justify-between mb-1.5 text-xs">
                                <span className="text-[#4A5568]">Pool</span>
                                <span className="text-[#1C252E] font-medium">${Math.round(m.matched_so_far).toLocaleString()} of ${Math.round(m.max_match_amount).toLocaleString()}</span>
                            </div>
                            <div className="h-2 bg-[#F4F1EA] rounded-full overflow-hidden">
                                <div className="h-full bg-[#E53935] rounded-full" style={{ width: `${poolPct}%` }} />
                            </div>
                        </div>
                    );
                })}
            </div>

            {showForm && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)} data-testid="admin-match-modal">
                    <div className="bg-white rounded-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="font-heading text-2xl text-[#1C252E] font-medium">New match window</h3>
                            <button onClick={() => setShowForm(false)} className="p-2"><X className="w-4 h-4" /></button>
                        </div>
                        <form onSubmit={save} className="space-y-4">
                            <input required placeholder="Match name (e.g. Spring Match Week)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="match-form-name" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <input required placeholder="Sponsor name (e.g. ACME Corp)" value={form.sponsor} onChange={(e) => setForm({ ...form, sponsor: e.target.value })} data-testid="match-form-sponsor" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-1">Multiplier</label>
                                    <select required value={form.multiplier} onChange={(e) => setForm({ ...form, multiplier: e.target.value })} data-testid="match-form-multiplier" className="w-full rounded-xl border border-black/10 px-3 py-2.5 bg-white">
                                        <option value="2">2x — Double</option>
                                        <option value="3">3x — Triple</option>
                                        <option value="1.5">1.5x</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-1">Max match $</label>
                                    <input required type="number" min="100" value={form.max_match_amount} onChange={(e) => setForm({ ...form, max_match_amount: e.target.value })} data-testid="match-form-max" className="w-full rounded-xl border border-black/10 px-3 py-2.5" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-1">Starts</label>
                                    <input required type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="match-form-starts" className="w-full rounded-xl border border-black/10 px-3 py-2.5 text-sm" />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-1">Ends</label>
                                    <input required type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="match-form-ends" className="w-full rounded-xl border border-black/10 px-3 py-2.5 text-sm" />
                                </div>
                            </div>
                            <button type="submit" data-testid="match-form-submit" className="w-full bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full py-3 font-medium">
                                Create match window
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Admin;
